import { LaunchError } from "../../utils/errors";

import {
  createElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useTheme } from "@mui/material";
import { Magnet, PanelsLeftRight, Square } from "lucide-react";
import MagnetOffIcon from "../../features/creativeTemplates/editor/MagnetOffIcon";
import GuideOpacityControl from "../../features/creativeTemplates/editor/GuideOpacityControl";
import ContainerPage from "../../components/Containers/ContainerPage";
import Button from "../../components/Buttons/Button";
import ButtonMenu from "../../components/Buttons/ButtonMenu";
import SegmentedControl from "../../components/SegmentedControl/SegmentedControl";
import TextField from "../../components/Form/TextField";
import Loader from "../../components/Loaders/Loader";
import { useDispatchApp } from "../../lib/redux";
import { useTranslationApp } from "../../lib/i18next";
import { getPathDatabaseByRole } from "../../actions/auth";
import {
  CREATIVE_TEMPLATE_DELETE_MODE,
  startAttachArtboardReference,
  startAttachOriginCreativeAndAnalyze,
  startDeleteCreativeTemplate,
  startListenerCreativeTemplate,
  startPreviewCreativeTemplateLayer,
  startGenerateCreativeTemplateLayerPrompt,
  startSaveCreativeTemplate,
  stopListenerCreativeTemplate,
} from "../../actions/creativeTemplates";
import { startResizeImage } from "../../actions/user";
import {
  startListenerTemplateActiveProduces,
  stopListenerCreativeTemplateProduce,
} from "../../actions/creativeTemplatesProduce";
import {
  startListenerMergedFonts,
  stopListenerMergedFonts,
} from "../../actions/fonts";
import { startGetBrandInformation } from "../../actions/adsCampaigns";
import { loadFontFaces } from "../../features/creativeTemplates/utils/fonts";
import {
  collectTextFontAssetRefsFromNodes,
  defaultTextStyleFromCatalog,
} from "../../features/creativeTemplates/utils/defaultTextFont";
import {
  TEMPLATES_MODAL_TYPE,
  useTemplatesModals,
} from "../../contexts/templatesModalsContext";
import { canWriteCreativeTemplates } from "../../features/creativeTemplates/utils/permissions";
import {
  countNodes,
  applyCanvasBackgroundLayout,
  applyNodeType,
  applyOverlayBox,
  applyOverlayBoxes,
  applySourceKind,
  copyNodesByIds,
  createDefaultNode,
  deleteNodeById,
  deleteNodesByIds,
  findNodePath,
  flattenTreeToBoxes,
  getAbsolutePx,
  imageOriginPatch,
  insertNodeAtOverlay,
  isCanvasBackgroundNode,
  isTextOriginPreviewKind,
  listNodeNames,
  pasteCopiedNodes,
  patchNodeById,
  resolveImageLayerPreview,
  resolveTextLayerPreview,
  slugifyName,
  textOriginPatch,
  uniqueName,
  resolveInsertParentID,
  setNodeVisible,
  setRootCanvasBackground,
  syncVariablesFromArtboards,
} from "../../features/creativeTemplates/utils/layerTree";
import { DEFAULT_SHADOW_ANGLE } from "../../features/creativeTemplates/utils/textStyle";
import {
  applyCanvasBackgroundToArtboards,
  applySourceKindToArtboards,
  copySourceGraphIntoEmptyArtboards,
  createEmptyArtboard,
  findCopySourceArtboard,
  getArtboard,
  getArtboardLabel,
  getArtboardReference,
  getReferenceAssetKey,
  hydrateArtboards,
  isolateCarouselSlideGuides,
  filterIncomingCarouselAssets,
  listArtboardIDs,
  listUncoveredNamesFromOtherArtboards,
  nextCustomArtboardID,
  patchActiveArtboardGraph,
  resolveActiveArtboardID,
  inferImplicitCanvasBackgroundNodeID,
  seedArtboard,
} from "../../features/creativeTemplates/utils/artboards";
import {
  addSlide,
  alignSlideFormats,
  convertToLayoutKind,
  countAllLayoutNodes,
  duplicateSlide,
  fanOutFormat,
  flattenCellsToArtboards,
  flushActiveCell,
  getLayoutVariableMismatches,
  getSlideByID,
  hydrateLayoutFromDoc,
  isCarouselLayout,
  isProcessingAnalysis,
  listAllCells,
  listProcessingSlideIDs,
  parseCellKey,
  mapSlideArtboards,
  patchSlideArtboards,
  removeFormatFromSlides,
  removeSlide,
  reorderSlides,
  resolveActiveSlideID,
  resolveLayoutKind,
} from "../../features/creativeTemplates/utils/slides";
import {
  getContinuousSlideCount,
  getContinuousSlideWidth,
  listSeamOffsets,
} from "../../features/creativeTemplates/utils/continuousStrip";
import {
  addSlideToArtboards,
  buildContinuousPresetArtboard,
  clearStripBackgroundCrop,
  hasContentInArtboardsSlideZone,
  removeSlideFromArtboards,
  stripToSlideArtboards,
} from "../../features/creativeTemplates/utils/continuousStripArtboards";
import ContinuousSlideChips from "../../features/creativeTemplates/editor/ContinuousSlideChips";
import ContinuousSetupModal from "../../features/creativeTemplates/editor/ContinuousSetupModal";
import { applySiblingDisplayOrder } from "../../features/creativeTemplates/instance/instanceGraph";
import TemplateCanvasOverlay from "../../features/creativeTemplates/editor/TemplateCanvasOverlay";
import ArtboardSwitcher from "../../features/creativeTemplates/editor/ArtboardSwitcher";
import SlideSwitcher from "../../features/creativeTemplates/editor/SlideSwitcher";
import { TemplateVariablesMismatchAlertContent } from "../../features/creativeTemplates/editor/TemplateVariablesMismatchTable";
import {
  applySessionLayerTryPreview,
  base64PngToBlobUrl,
  collectPreviewArtboardAssets,
  collectPreviewSampleRow,
  layerTryPreviewKey,
  parseLayerTryPreviewKey,
  pickPreviewLayerNode,
  revokeLayerTryPreview,
  revokeLayerTryPreviews,
} from "../../features/creativeTemplates/editor/layerTryPreview";
import { TemplateTransparencyGapAlertContent } from "../../features/creativeTemplates/editor/TemplateTransparencyGapAlert";
import { findArtboardsWithTransparencyGaps } from "../../features/creativeTemplates/utils/artboardCoverage";
import LayerTreePanel from "../../features/creativeTemplates/editor/LayerTreePanel";
import LayerPropertiesPanel from "../../features/creativeTemplates/editor/LayerPropertiesPanel";
import { alignSelectedBoxes } from "../../features/creativeTemplates/editor/alignSelectedLayers";
import {
  sidebarResizeHandleSx,
  useResizableSidebarWidth,
} from "../../features/creativeTemplates/editor/useResizableSidebarWidth";
import { useTemplateEditorHistory } from "../../features/creativeTemplates/editor/useTemplateEditorHistory";
import useTemplateEditorPreview, {
  TEMPLATE_EDITOR_PREVIEW_SCOPE,
  isTemplateEditorPreviewLoading,
} from "../../features/creativeTemplates/editor/useTemplateEditorPreview";
import TemplateEditorPreviewVariablesModal from "../../features/creativeTemplates/editor/TemplateEditorPreviewVariablesModal";
import TemplateEditorPreviewImageModal from "../../features/creativeTemplates/editor/TemplateEditorPreviewImageModal";
import {
  nextLayerSelection,
  nextMarqueeSelection,
  resolveOperationIds,
} from "../../features/creativeTemplates/editor/canvasLayerSelection";
import {
  CANVAS_CLIPBOARD_KIND,
  consumeClipboardPasteCount,
  getCanvasClipboard,
  isCopyHotkey,
  isDeleteHotkey,
  isNativeEditableTarget,
  isPasteHotkey,
  blurActiveEditable,
  setCanvasClipboard,
} from "../../features/creativeTemplates/utils/canvasClipboard";
import { ConfirmAlert, SimpleAlert } from "../../components/Alerts/Alerts";
import { getIsBlockedActions } from "../../actions/getters";
import { useCreditActionCosts } from "../../hooks/useCreditActionCosts";
import { confirmAnalyzeDetectsText } from "../../utils/confirmAnalyzeDetectsText";
import {
  getImageDimensionsFromFile,
  getImageDimensionsFromUrl,
  readImageDimensionsFromUrl,
} from "../../features/creativeTemplates/utils/getImageDimensionsFromFile";
import {
  fitImageOverlayBox,
  sizeOverlayBoxToNaturalImage,
} from "../../features/creativeTemplates/utils/fitImageOverlayBox";
import useTextHighlightSelection, {
  applyIfHighlightField,
} from "../../features/creativeTemplates/editor/useTextHighlightSelection";
import {
  areHighlightListsEqual,
  highlightSourceText,
  retargetHighlightsForTextEdit,
} from "../../features/creativeTemplates/utils/textHighlights";
import {
  listStoredInlineReplacements,
  resolveInlineReplacementUrls,
} from "../../features/creativeTemplates/utils/textInlineReplacements";
import { isLayerizeImageTooSmall } from "../../features/creativeTemplates/utils/layerizeImageConstraints";
import ModalGalleryAssets from "../../components/Modal/ModalGalleryAssets";
import ModalGallery from "../../components/Modal/ModalGallery";
import ModalWatchGuideVideo from "../../components/Modal/ModalWatchGuideVideo";
import { getKeyDatabase } from "../../services/public";
import { getExtensionFile, blobUrlToFile } from "../../utils/string";
import {
  ALERT_ICON_TYPE_SUCCESS,
  BORDER_RADIUS_BUTTON,
  CREATIVE_TEMPLATE_ANALYSIS_STATUS,
  CREATIVE_TEMPLATE_ARTBOARD_PRESETS,
  CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID,
  CREATIVE_TEMPLATE_CONTINUOUS_CAROUSEL_ENABLED,
  CREATIVE_TEMPLATE_CONTINUOUS_SLIDE_MAX,
  CREATIVE_TEMPLATE_CONTINUOUS_SLIDE_MIN,
  CREATIVE_TEMPLATE_LAYOUT_KIND,
  IMAGE_FORMATS,
  MEDIA_DIMENSIONS,
} from "../../utils/constants";
import { buildResizeImagePayload } from "../../utils/buildResizeImagePayload";
import { extractResizeResultUrls } from "../../utils/creativeJobResultUrls";
import {
  ARE_YOU_SURE,
  BUTTON_CLOSE,
  TEMPLATES_USAGE_GUIDE,
  TEMPLATES_GUIDE_VIDEO_URL,
  BUTTON_CONTINUE,
  BUTTON_GO_BACK,
  FIELD_NAME_REQUIRED,
  LOADING_INFORMATION,
  TEMPLATES_ANALYZE_ERROR,
  TEMPLATES_ANALYZE_IMAGE_TOO_SMALL,
  TEMPLATES_ANALYZE_RETRY,
  TEMPLATES_ANALYZING_LOCK,
  TEMPLATES_BUTTON_UPLOAD,
  TEMPLATES_UPLOAD_FROM_DEVICE,
  TEMPLATES_UPLOAD_FROM_GALLERY,
  TEMPLATES_EMPTY_CANVAS_HINT,
  TEMPLATES_EMPTY_ADD_LAYER,
  TEMPLATES_EDIT_PREVIOUS,
  TEMPLATES_NEW_FORMAT_HINT,
  TEMPLATES_UPLOAD_REFERENCE,
  TEMPLATES_CHANGE_REFERENCE,
  TEMPLATES_PRODUCE_PREVIEW,
  TEMPLATES_PRODUCE_PREVIEW_THIS_SLIDE,
  TEMPLATES_PRODUCE_PREVIEW_TOOLTIP,
  TEMPLATES_PRODUCE_PREVIEW_WHOLE_CAROUSEL,
  TEMPLATES_GUIDE_HINT,
  TEMPLATES_ALIGNMENT_GUIDES,
  TEMPLATES_BACKGROUND_LAYER,
  TEMPLATES_CONFIRM_ADD_FORMAT,
  TEMPLATES_CONFIRM_ADD_FORMAT_DESCRIPTION,
  TEMPLATES_CONFIRM_AI_RESIZE_BACKGROUND,
  TEMPLATES_CONFIRM_AI_RESIZE_BACKGROUND_DESCRIPTION,
  TEMPLATES_AI_RESIZE_BACKGROUND_CONFIRM,
  TEMPLATES_CONFIRM_REMOVE_FORMAT_DESCRIPTION,
  TEMPLATES_CONFIRM_REMOVE_FORMAT_TITLE,
  TEMPLATES_KEEP_EDITING,
  TEMPLATES_DELETE_SLIDE,
  TEMPLATES_CLOSE_UNSAVED_DESCRIPTION,
  TEMPLATES_LAYOUT_KIND_CAROUSEL,
  TEMPLATES_LAYOUT_KIND_CONTINUOUS,
  TEMPLATES_LAYOUT_KIND_INDIVIDUAL,
  TEMPLATES_LAYOUT_KIND_INDIVIDUAL_TOOLTIP,
  TEMPLATES_LAYOUT_KIND_CONTINUOUS_TOOLTIP,
  TEMPLATES_LAYOUT_KIND_TO_INDIVIDUAL_HINT,
  TEMPLATES_LAYOUT_KIND_TO_CONTINUOUS_HINT,
  TEMPLATES_LAYOUT_KIND_TO_INDIVIDUAL_BLOCKED,
  TEMPLATES_LAYOUT_KIND_TO_CONTINUOUS_BLOCKED,
  TEMPLATES_CONFIRM_DELETE_STRIP_SLIDE_DESCRIPTION,
  TEMPLATES_LAYOUT_KIND_IMAGE,
  TEMPLATES_NAME,
  TEMPLATES_NEW_LAYER,
  TEMPLATES_PRODUCE_ADD_IMAGE,
  TEMPLATES_PRODUCE_ADD_TEXT,
  TEMPLATES_REMOVE_FORMAT,
  TEMPLATES_FORMATS_ROW,
  TEMPLATES_SAVE,
  TEMPLATES_SAVE_BLOCKED_PRODUCING,
  TEMPLATES_SAVE_ERROR,
  TEMPLATES_SLIDE_LABEL,
  TEMPLATES_PROMPT,
  TEMPLATES_TRY_LAYER_ERROR,
  TEMPLATES_GENERATE_PROMPT_ERROR,
  TEMPLATES_SAVE_SUCCESS,
  TEMPLATES_SAVE_VARIABLES_MISMATCH_DESCRIPTION_CAROUSEL,
  TEMPLATES_SAVE_VARIABLES_MISMATCH_TITLE,
  TEMPLATES_SAVE_VARIABLES_MISMATCH_TITLE_CAROUSEL,
  TEMPLATES_SAVE_TRANSPARENCY_GAP_TITLE,
  TEMPLATES_SAVE_PRODUCE_CANCEL,
  TEMPLATES_SAVE_PRODUCE_CONFIRM,
  TEMPLATES_SAVE_PRODUCE_DESCRIPTION,
  TEMPLATES_SAVE_PRODUCE_TITLE,
  TEMPLATES_UNAUTHORIZED,
  TEMPLATES_UNTITLED,
  TEMPLATES_UPLOAD_ERROR,
  WARNING,
} from "../../i18n/keysTranslations";

const ADD_FORMAT_HINT_STORAGE_PREFIX = "bz.creativeTemplate.addFormatHint.";
const TEMPLATE_NAME_AND_LAYERS_WIDTH = 360;
const TEMPLATE_HEADER_CONTROL_HEIGHT = 53;
const FALLBACK_ARTBOARD = { width: 1, height: 1, nodeLayouts: {} };
const shownAddFormatHintByTemplateID = new Set();

const hasShownAddFormatHint = (templateID) => {
  if (!templateID) return false;
  if (shownAddFormatHintByTemplateID.has(templateID)) return true;
  try {
    return (
      window.localStorage.getItem(
        `${ADD_FORMAT_HINT_STORAGE_PREFIX}${templateID}`
      ) === "1"
    );
  } catch {
    return false;
  }
};

const markAddFormatHintShown = (templateID) => {
  if (!templateID) return;
  shownAddFormatHintByTemplateID.add(templateID);
  try {
    window.localStorage.setItem(
      `${ADD_FORMAT_HINT_STORAGE_PREFIX}${templateID}`,
      "1"
    );
  } catch {
    // ignore quota / private mode
  }
};

const INSPECTOR_TEXT_FIELD_KEYS = new Set([
  "name",
  "fixedValue",
  "originText",
  "expression",
  "promptTemplate",
]);

const emptyInspector = {
  layerName: "",
  type: "text",
  sourceKind: "fixed",
  fixedValue: "",
  originText: "",
  variable: "",
  expression: "",
  promptTemplate: "",
  assetRef: "",
  fontAssetRef: "",
  fontFaceRef: "",
  fontWeight: "",
  fontSize: "",
  lineHeight: "",
  letterSpacing: "",
  strokeWidth: "",
  strokeColor: "#000000",
  shadowColor: "#000000",
  shadowAngle: DEFAULT_SHADOW_ANGLE,
  shadowDistance: "",
  shadowBlur: "",
  backgroundPadding: "",
  backgroundRadius: "",
  backgroundColor: "#000000",
  textStyle: "normal",
  color: "",
  align: "left",
  orientation: "horizontal",
  maximumLines: "",
  minimumLines: "",
  frameWidth: "",
  frameHeight: "",
  clipContent: "false",
  isBackground: false,
  locked: false,
  posX: "",
  posY: "",
  posW: "",
  posH: "",
};

const inspectorFontSize = (fontSize) => {
  if (fontSize == null || fontSize === "") return "";
  const parsed = Number(fontSize);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : "";
};

const inspectorFromNode = ({ node, layout }) => ({
  ...emptyInspector,
  layerName: node.name,
  type: node.type,
  sourceKind: node.source?.kind || "",
  fixedValue: node.source?.value || "",
  originText: node.originText || "",
  variable: node.source?.variable || "",
  expression: node.source?.expression || "",
  promptTemplate: node.source?.promptTemplate || "",
  assetRef: node.source?.assetRef || "",
  fontAssetRef: node.style?.fontAssetRef || "",
  fontFaceRef: node.style?.fontFaceRef || "",
  fontWeight: node.style?.fontWeight ?? "",
  fontSize: inspectorFontSize(node.style?.fontSize),
  lineHeight: node.style?.lineHeight ?? "",
  letterSpacing: node.style?.letterSpacing ?? "",
  strokeWidth: node.style?.strokeWidth ?? "",
  strokeColor: node.style?.strokeColor || "#000000",
  shadowColor: node.style?.shadowColor || "#000000",
  shadowAngle: node.style?.shadowAngle ?? DEFAULT_SHADOW_ANGLE,
  shadowDistance: node.style?.shadowDistance ?? "",
  shadowBlur: node.style?.shadowBlur ?? "",
  backgroundPadding: node.style?.backgroundPadding ?? "",
  backgroundRadius: node.style?.backgroundRadius ?? "",
  backgroundColor: node.style?.backgroundColor || "#000000",
  textStyle: node.style?.textStyle || "normal",
  color: node.style?.color || "",
  align: node.style?.align || "left",
  orientation: node.style?.orientation || "horizontal",
  maximumLines: node.overflow?.maximumLines ?? "",
  minimumLines: node.overflow?.minimumLines ?? "",
  frameWidth: node.componentFrame?.width ?? "",
  frameHeight: node.componentFrame?.height ?? "",
  clipContent: node.clipContent ? "true" : "false",
  isBackground: Boolean(node.isBackground),
  locked: Boolean(node.locked),
  posX: layout?.x ?? "",
  posY: layout?.y ?? "",
  posW: layout?.width ?? "",
  posH: layout?.height ?? "",
});

const nextAssetKey = ({ name, assets, artboards, slides, existingRef }) => {
  if (
    existingRef &&
    existingRef !== "reference" &&
    !existingRef.startsWith("reference_")
  ) {
    return existingRef;
  }
  const reservedKeys = [
    "reference",
    ...Object.keys(artboards || {}).map((artboardID) =>
      getReferenceAssetKey({ artboardID })
    ),
    ...(slides || []).flatMap((slide) =>
      Object.keys(slide?.artboards || {}).map((artboardID) =>
        getReferenceAssetKey({ artboardID, slideID: slide.id })
      )
    ),
  ];
  return uniqueName({
    name: slugifyName({ name, fallback: "layer" }),
    existingNames: [...Object.keys(assets || {}), ...reservedKeys],
  });
};

const imageMimeFromUrl = (url) => {
  const extension = getExtensionFile(url);
  if (extension === "jpg" || extension === "jpeg") return "image/jpeg";
  if (extension === "png") return "image/png";
  if (extension === "webp") return "image/webp";
  return "image/png";
};

const ASSETS_GALLERY_MODE = {
  ADD: "add",
  REPLACE: "replace",
  INLINE_REPLACEMENT: "inlineReplacement",
};

const defaultLayerBox = (type) => {
  switch (type) {
    case "text":
      return { nx: 0.08, ny: 0.08, nw: 0.4, nh: 0.12 };
    case "group":
      return { nx: 0.08, ny: 0.08, nw: 0.32, nh: 0.32 };
    case "image":
      return { nx: 0.12, ny: 0.12, nw: 0.28, nh: 0.28 };
    default: {
      const exhaustive = type;
      throw new Error(`Unhandled layer type: ${exhaustive}`);
    }
  }
};

const TemplateEditor = ({
  templateID = "",
  isProvisionalCreate = false,
  returnToProduce = false,
  initialPreviewProduceID = "",
  previewRequestKey = "",
  onBackToList = () => {},
  onRegisterCloseHandler = null,
}) => {
  const { t, i18n } = useTranslationApp();
  const theme = useTheme();
  const dispatch = useDispatchApp();
  const {
    ADS_CAMPAIGN_ANALYZE_IMAGE_LAYERS_ACTION,
    ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
  } = useCreditActionCosts();
  const { openTemplateProduce, stack } = useTemplatesModals();
  const produceAlreadyOpen = stack.some(
    (entry) => entry.type === TEMPLATES_MODAL_TYPE.PRODUCE
  );
  const closeToProduce = returnToProduce || produceAlreadyOpen;
  const { role, userID } = dispatch(getPathDatabaseByRole());
  const canWrite = canWriteCreativeTemplates({ role });

  const [isLoading, setIsLoading] = useState(true);
  const [isAssetsGalleryOpen, setIsAssetsGalleryOpen] = useState(false);
  const [isCreativeGalleryOpen, setIsCreativeGalleryOpen] = useState(false);
  const [assetsGalleryMode, setAssetsGalleryMode] = useState(
    ASSETS_GALLERY_MODE.ADD
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isTemplateProducing, setIsTemplateProducing] = useState(false);
  const activeProducesListenerRef = useRef(null);
  const [uploadingSlideIDs, setUploadingSlideIDs] = useState([]);
  const [uploadingGuideSlideIDs, setUploadingGuideSlideIDs] = useState([]);
  const [resizingBackgroundKeys, setResizingBackgroundKeys] = useState({});
  const [isGuideVisible, setIsGuideVisible] = useState(false);
  const [isGuideVideoOpen, setIsGuideVideoOpen] = useState(false);
  const [alignmentGuidesEnabled, setAlignmentGuidesEnabled] = useState(true);
  const [guideOpacity, setGuideOpacity] = useState(0);
  const {
    width: layersSidebarWidth,
    onResizePointerDown: onResizeLayersSidebar,
  } = useResizableSidebarWidth({
    defaultWidth: TEMPLATE_NAME_AND_LAYERS_WIDTH,
    minWidth: 220,
    maxWidth: 520,
  });
  const [artboards, setArtboards] = useState({});
  const [slides, setSlides] = useState([]);
  const [layoutKind, setLayoutKind] = useState(
    CREATIVE_TEMPLATE_LAYOUT_KIND.SINGLE
  );
  const [continuousSlideCount, setContinuousSlideCount] = useState(
    CREATIVE_TEMPLATE_CONTINUOUS_SLIDE_MIN
  );
  const [activeArtboardID, setActiveArtboardID] = useState(
    CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID
  );
  const [activeSlideID, setActiveSlideID] = useState("");
  const [nodes, setNodes] = useState({});
  const [variables, setVariables] = useState({});
  const [assets, setAssets] = useState({});
  const [analysis, setAnalysis] = useState(null);
  const [analysisBySlide, setAnalysisBySlide] = useState({});
  const [selectedID, setSelectedID] = useState(null);
  const [selectedIDs, setSelectedIDs] = useState([]);
  const [editingTextID, setEditingTextID] = useState(null);
  const [expandedIDs, setExpandedIDs] = useState({});
  const [layerTryPreviews, setLayerTryPreviews] = useState({});
  const [tryingLayerKeys, setTryingLayerKeys] = useState({});
  const [tryImageModal, setTryImageModal] = useState(null);
  const [isContinuousSetupOpen, setIsContinuousSetupOpen] = useState(false);
  const selectedIDRef = useRef(null);
  const selectedIDsRef = useRef([]);
  const graphRef = useRef({ nodes: {}, nodeLayouts: {} });
  const persistHighlightPruneRef = useRef(() => {});
  const inlineReplacementSlotIDRef = useRef(null);
  const artboardsRef = useRef({});
  const slidesRef = useRef([]);
  const layoutKindRef = useRef(CREATIVE_TEMPLATE_LAYOUT_KIND.SINGLE);
  const continuousSlideCountRef = useRef(
    CREATIVE_TEMPLATE_CONTINUOUS_SLIDE_MIN
  );
  const canvasApiRef = useRef(null);
  const activeArtboardIDRef = useRef(CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID);
  const activeSlideIDRef = useRef("");
  const assetsRef = useRef({});
  const layerTryPreviewsRef = useRef({});
  const tryingLayerKeysRef = useRef({});
  const tryImageModalUrlRef = useRef("");
  const [generatingPromptKeys, setGeneratingPromptKeys] = useState({});
  const generatingPromptKeysRef = useRef({});
  const variablesRef = useRef({});
  const listenerRef = useRef(null);
  const fontListenerRefs = useRef({ commonRef: null, advertiserRef: null });
  const fontFamiliesRef = useRef([]);
  const brandFontFamilyRef = useRef("");
  const hasLoadedRef = useRef(false);
  const analysisStatusRef = useRef(null);
  const analysisBySlideRef = useRef({});
  const fileInputRef = useRef(null);
  const referenceFileInputRef = useRef(null);
  const isLockedRef = useRef(false);
  const previousArtboardIDRef = useRef(null);
  const copyPasteHandlersRef = useRef({
    copy: () => false,
    paste: () => false,
    delete: () => false,
  });

  const getHistorySnapshot = () => ({
    nodes: graphRef.current.nodes,
    artboards: artboardsRef.current,
    slides: slidesRef.current,
    layoutKind: layoutKindRef.current,
    continuousSlideCount: continuousSlideCountRef.current,
    activeArtboardID: activeArtboardIDRef.current,
    activeSlideID: activeSlideIDRef.current,
    variables: variablesRef.current,
    assets: assetsRef.current,
    selectedID: selectedIDRef.current,
    selectedIDs: selectedIDsRef.current,
  });

  const applyHistorySnapshot = (snapshot) => {
    const nextLayoutKind =
      snapshot.layoutKind || CREATIVE_TEMPLATE_LAYOUT_KIND.SINGLE;
    const nextSlides = snapshot.slides || [];
    const nextArtboards = snapshot.artboards || {};
    const nextActiveSlideID = resolveActiveSlideID({
      slides: nextSlides,
      activeSlideID: snapshot.activeSlideID,
    });
    const nextActiveID = resolveActiveArtboardID({
      artboards: nextArtboards,
      activeArtboardID: snapshot.activeArtboardID,
    });
    const nextNodes =
      nextArtboards[nextActiveID]?.nodes || snapshot.nodes || {};
    const nextLayouts = nextArtboards[nextActiveID]?.nodeLayouts || {};
    graphRef.current = {
      nodes: nextNodes,
      nodeLayouts: nextLayouts,
    };
    artboardsRef.current = nextArtboards;
    slidesRef.current = nextSlides;
    layoutKindRef.current = nextLayoutKind;
    continuousSlideCountRef.current =
      snapshot.continuousSlideCount || CREATIVE_TEMPLATE_CONTINUOUS_SLIDE_MIN;
    activeArtboardIDRef.current = nextActiveID;
    activeSlideIDRef.current = nextActiveSlideID;
    variablesRef.current = snapshot.variables;
    assetsRef.current = snapshot.assets;
    selectedIDRef.current = snapshot.selectedID;
    selectedIDsRef.current =
      snapshot.selectedIDs ||
      (snapshot.selectedID ? [snapshot.selectedID] : []);
    setNodes(nextNodes);
    setArtboards(nextArtboards);
    setSlides(nextSlides);
    setLayoutKind(nextLayoutKind);
    setContinuousSlideCount(continuousSlideCountRef.current);
    setActiveArtboardID(nextActiveID);
    setActiveSlideID(nextActiveSlideID);
    setVariables(snapshot.variables);
    setAssets(snapshot.assets);
    setSelectedID(snapshot.selectedID);
    setSelectedIDs(selectedIDsRef.current);
  };

  const { push, endCoalesce, undo, redo, clear } = useTemplateEditorHistory({
    getSnapshot: getHistorySnapshot,
    applySnapshot: applyHistorySnapshot,
  });
  const historyClearRef = useRef(clear);
  historyClearRef.current = clear;

  const {
    control,
    reset,
    getValues,
    setValue,
    trigger,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      ...emptyInspector,
    },
  });

  const isCarousel = isCarouselLayout({ layoutKind, slides });
  const isContinuous =
    resolveLayoutKind({ layoutKind, slides }) ===
    CREATIVE_TEMPLATE_LAYOUT_KIND.CONTINUOUS_CAROUSEL;
  const formatSwitcherArtboards = useMemo(() => {
    if (!isCarousel) return artboards;
    const aligned = alignSlideFormats({ slides, artboards });
    return (
      getSlideByID({
        slides: aligned,
        slideID: activeSlideID,
      })?.artboards || artboards
    );
  }, [isCarousel, slides, artboards, activeSlideID]);
  const activeSlideAnalysis = isCarousel
    ? analysisBySlide[activeSlideID] || null
    : analysis;
  const isAnalyzing = isProcessingAnalysis({ analysis: activeSlideAnalysis });
  const isAnalysisError =
    activeSlideAnalysis?.status === CREATIVE_TEMPLATE_ANALYSIS_STATUS.ERROR;
  const isLocked = !canWrite || isAnalyzing;
  const lockMessage = isAnalyzing
    ? TEMPLATES_ANALYZING_LOCK
    : TEMPLATES_UNAUTHORIZED;
  const analyzingSlideIDs = listProcessingSlideIDs({ analysisBySlide });
  const uploadSlideKey = isCarousel ? activeSlideID : "";
  const isUploading = uploadingSlideIDs.includes(uploadSlideKey);
  const guideUploadKey = isCarousel ? activeSlideID : "__single__";
  const isUploadingGuide = uploadingGuideSlideIDs.includes(guideUploadKey);
  const activeArtboard =
    getArtboard({
      artboards,
      artboardID: activeArtboardID,
    }) || FALLBACK_ARTBOARD;
  const nodeLayouts = useMemo(
    () => activeArtboard.nodeLayouts || FALLBACK_ARTBOARD.nodeLayouts,
    [activeArtboard.nodeLayouts]
  );
  const artboard = {
    width: activeArtboard.width || 1,
    height: activeArtboard.height || 1,
  };
  const guideArtboard =
    (isCarousel
      ? getSlideByID({ slides, slideID: activeSlideID })?.artboards?.[
          activeArtboardID
        ]
      : null) || activeArtboard;
  const canvasImageUrl =
    getArtboardReference({
      artboard: guideArtboard,
      artboardID: activeArtboardID,
      assets,
      allowLegacyReference: !isCarousel,
    })?.url || "";
  const isEmptyCanvas =
    !canvasImageUrl && countNodes({ nodes }) === 0 && !isAnalyzing;
  const copySourceArtboard = findCopySourceArtboard({
    artboards,
    excludeID: activeArtboardID,
    preferredID: previousArtboardIDRef.current,
    width: artboard.width,
    height: artboard.height,
  });
  const showNewFormatEmpty = isEmptyCanvas && Boolean(copySourceArtboard);

  const {
    textSelection,
    textSelectionRef,
    keepTextEditingRef,
    setTextSelection,
    onTextSelectionChange,
    styleForHighlightField,
    isTextRangeSelected,
    inspectorOverlay,
  } = useTextHighlightSelection({
    selectedID,
    editingTextID,
    canvasSelector: "[data-template-canvas]",
    nodesVersion: nodes,
    getNode: (nodeID) =>
      nodeID
        ? findNodePath({ nodes: graphRef.current.nodes, nodeID })?.node
        : null,
    persistHighlights: (payload) => persistHighlightPruneRef.current(payload),
  });

  const skipInspectorFormResetRef = useRef({ skip: false, nodeID: null });
  const onChangeFieldRef = useRef(null);

  useEffect(() => {
    selectedIDRef.current = selectedID;
  }, [selectedID]);

  useEffect(() => {
    selectedIDsRef.current = selectedIDs;
  }, [selectedIDs]);

  useEffect(() => {
    graphRef.current = { nodes, nodeLayouts };
  }, [nodes, nodeLayouts]);

  useEffect(() => {
    artboardsRef.current = artboards;
    if (templateID && listArtboardIDs({ artboards }).length > 1) {
      markAddFormatHintShown(templateID);
    }
  }, [artboards, templateID]);

  useEffect(() => {
    slidesRef.current = slides;
  }, [slides]);

  useEffect(() => {
    layoutKindRef.current = layoutKind;
  }, [layoutKind]);

  useEffect(() => {
    activeArtboardIDRef.current = activeArtboardID;
  }, [activeArtboardID]);

  useEffect(() => {
    activeSlideIDRef.current = activeSlideID;
  }, [activeSlideID]);

  useEffect(() => {
    assetsRef.current = assets;
  }, [assets]);

  useEffect(() => {
    layerTryPreviewsRef.current = layerTryPreviews;
  }, [layerTryPreviews]);

  useEffect(() => {
    tryingLayerKeysRef.current = tryingLayerKeys;
  }, [tryingLayerKeys]);

  useEffect(
    () => () => {
      revokeLayerTryPreviews(layerTryPreviewsRef.current);
      if (tryImageModalUrlRef.current) {
        URL.revokeObjectURL(tryImageModalUrlRef.current);
      }
    },
    []
  );

  useEffect(() => {
    setLayerTryPreviews((current) => {
      const next = { ...current };
      let changed = false;
      Object.keys(next).forEach((key) => {
        const { artboardID, nodeID } = parseLayerTryPreviewKey(key);
        if (artboardID !== activeArtboardID) return;
        if (findNodePath({ nodes, nodeID })) return;
        revokeLayerTryPreview(next[key]);
        delete next[key];
        changed = true;
      });
      if (!changed) return current;
      layerTryPreviewsRef.current = next;
      return next;
    });
  }, [activeArtboardID, nodes]);

  useEffect(() => {
    variablesRef.current = variables;
  }, [variables]);

  useEffect(() => {
    isLockedRef.current = isLocked;
  }, [isLocked]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (isLockedRef.current) return;
      if (isNativeEditableTarget(event.target)) return;
      if (isDeleteHotkey(event)) {
        if (copyPasteHandlersRef.current.delete()) {
          event.preventDefault();
        }
        return;
      }
      const key = event.key.toLowerCase();
      const hasModifier = event.ctrlKey || event.metaKey;
      if (!hasModifier) return;

      if (isCopyHotkey(event)) {
        if (copyPasteHandlersRef.current.copy()) {
          event.preventDefault();
        }
        return;
      }
      if (isPasteHotkey(event)) {
        if (copyPasteHandlersRef.current.paste()) {
          event.preventDefault();
        }
        return;
      }
      if (key === "z" && event.shiftKey) {
        event.preventDefault();
        redo();
        return;
      }
      if (key === "z") {
        event.preventDefault();
        undo();
        return;
      }
      if (key === "y" && event.ctrlKey && !event.metaKey) {
        event.preventDefault();
        redo();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [redo, undo]);

  useEffect(() => {
    dispatch(
      startListenerMergedFonts({
        onUpdatedDataCallback: (fonts) => {
          fontFamiliesRef.current = fonts || [];
          loadFontFaces({ fonts });
        },
        onReferenceAvailable: (refs) => {
          fontListenerRefs.current = refs;
        },
      })
    );
    return () => {
      dispatch(
        stopListenerMergedFonts({ listenerRefs: fontListenerRefs.current })
      );
    };
  }, [dispatch]);

  useEffect(() => {
    const loadBrand = async () => {
      const response = await dispatch(startGetBrandInformation());
      if (response.ok) {
        brandFontFamilyRef.current =
          response.data?.businessTypography?.fontFamily || "";
      }
    };
    loadBrand();
  }, [dispatch]);

  useEffect(() => {
    hasLoadedRef.current = false;
    analysisStatusRef.current = null;
    analysisBySlideRef.current = {};

    if (!templateID) {
      onBackToList();
      return undefined;
    }

    const applySnapshot = ({ card, doc, overlayActiveNodes = false }) => {
      const layout = hydrateLayoutFromDoc({ doc });
      const carouselLayout = isCarouselLayout({
        layoutKind: layout.layoutKind,
        slides: layout.slides,
      });
      const nextArtboards = carouselLayout
        ? hydrateArtboards({
            artboards: layout.artboards || {},
            nodes: {},
            allowSharedNodes: false,
          })
        : copySourceGraphIntoEmptyArtboards({
            artboards: hydrateArtboards({
              artboards: layout.artboards || {},
              nodes: doc?.nodes || {},
            }),
            sourceArtboards: doc?.artboards || {},
          });
      const nextActiveID = resolveActiveArtboardID({
        artboards: nextArtboards,
        activeArtboardID: doc?.editor?.activeArtboardID,
      });
      const rootArtboardGraph = doc?.artboards?.[nextActiveID] || {};
      const withOverlay =
        overlayActiveNodes && !carouselLayout
          ? patchActiveArtboardGraph({
              artboards: nextArtboards,
              artboardID: nextActiveID,
              nodes: rootArtboardGraph.nodes || doc?.nodes || {},
              nodeLayouts: rootArtboardGraph.nodeLayouts,
            })
          : nextArtboards;
      const nextVariables = doc?.variables || {};
      const nextAssets = doc?.assets || {};
      reset({
        name: card?.name || doc?.name || "",
        ...emptyInspector,
      });
      const patchedSlides = carouselLayout
        ? patchSlideArtboards({
            slides: layout.slides || [],
            slideID: layout.activeSlideID,
            artboards: withOverlay,
          })
        : layout.slides || [];
      const alignedSlides = carouselLayout
        ? alignSlideFormats({
            slides: patchedSlides,
            artboards: withOverlay,
          })
        : patchedSlides;
      const isolatedGuides = carouselLayout
        ? isolateCarouselSlideGuides({
            slides: alignedSlides,
            assets: nextAssets,
          })
        : { slides: alignedSlides, assets: nextAssets };
      const nextSlides = isolatedGuides.slides;
      const resolvedAssets = isolatedGuides.assets;
      const baseWorkingArtboards = carouselLayout
        ? getSlideByID({
            slides: nextSlides,
            slideID: layout.activeSlideID,
          })?.artboards || withOverlay
        : withOverlay;
      const workingArtboards =
        layout.layoutKind === CREATIVE_TEMPLATE_LAYOUT_KIND.CONTINUOUS_CAROUSEL
          ? clearStripBackgroundCrop({ artboards: baseWorkingArtboards })
          : baseWorkingArtboards;
      const workingActiveID = resolveActiveArtboardID({
        artboards: workingArtboards,
        activeArtboardID: nextActiveID,
      });
      const workingNodes = workingArtboards[workingActiveID]?.nodes || {};
      const workingLayouts =
        workingArtboards[workingActiveID]?.nodeLayouts || {};
      graphRef.current = { nodes: workingNodes, nodeLayouts: workingLayouts };
      artboardsRef.current = workingArtboards;
      slidesRef.current = nextSlides;
      layoutKindRef.current = layout.layoutKind;
      continuousSlideCountRef.current = getContinuousSlideCount({
        layoutConfigurations: layout.layoutConfigurations,
      });
      activeArtboardIDRef.current = workingActiveID;
      activeSlideIDRef.current = layout.activeSlideID || "";
      variablesRef.current = nextVariables;
      assetsRef.current = resolvedAssets;
      selectedIDRef.current = null;
      selectedIDsRef.current = [];
      setArtboards(workingArtboards);
      setSlides(nextSlides);
      setLayoutKind(layout.layoutKind);
      setContinuousSlideCount(continuousSlideCountRef.current);
      setActiveArtboardID(workingActiveID);
      setActiveSlideID(layout.activeSlideID || "");
      setNodes(workingNodes);
      setVariables(nextVariables);
      setAssets(resolvedAssets);
      setAnalysis(doc?.editor?.analysis || null);
      setAnalysisBySlide(doc?.editor?.analysisBySlide || {});
      analysisBySlideRef.current = doc?.editor?.analysisBySlide || {};
      setSelectedID(null);
      setSelectedIDs([]);
      setTextSelection(null);
      historyClearRef.current();
    };

    dispatch(
      startListenerCreativeTemplate({
        templateID,
        onUpdatedDataCallback: (value) => {
          if (!value) {
            onBackToList();
            return;
          }
          const nextStatus = value.doc?.editor?.analysis?.status || null;
          const previousStatus = analysisStatusRef.current;
          const nextBySlide = value.doc?.editor?.analysisBySlide || {};
          const previousBySlide = analysisBySlideRef.current || {};
          const carouselDoc = isCarouselLayout(value.doc);
          const changedSlideIDs = Object.keys({
            ...nextBySlide,
            ...previousBySlide,
          }).filter(
            (slideID) =>
              nextBySlide[slideID]?.status !==
                previousBySlide[slideID]?.status ||
              nextBySlide[slideID]?.jobID !== previousBySlide[slideID]?.jobID
          );
          const shouldReplaceGraph =
            !hasLoadedRef.current ||
            (!carouselDoc &&
              (nextStatus === CREATIVE_TEMPLATE_ANALYSIS_STATUS.PROCESSING ||
                previousStatus ===
                  CREATIVE_TEMPLATE_ANALYSIS_STATUS.PROCESSING ||
                nextStatus !== previousStatus));

          if (shouldReplaceGraph) {
            applySnapshot({
              card: value.card,
              doc: value.doc,
              overlayActiveNodes:
                nextStatus === CREATIVE_TEMPLATE_ANALYSIS_STATUS.PROCESSING ||
                previousStatus === CREATIVE_TEMPLATE_ANALYSIS_STATUS.PROCESSING,
            });
          } else if (carouselDoc && changedSlideIDs.length > 0) {
            const incomingCarouselAssets = filterIncomingCarouselAssets({
              assets: value.doc?.assets || {},
              slideIDs: changedSlideIDs,
            });
            const nextAssets = {
              ...assetsRef.current,
              ...incomingCarouselAssets,
            };
            assetsRef.current = nextAssets;
            setAssets(nextAssets);
            setAnalysis(value.doc?.editor?.analysis || null);
            setAnalysisBySlide(nextBySlide);
            analysisBySlideRef.current = nextBySlide;
            changedSlideIDs.forEach((slideID) => {
              const slide = getSlideByID({
                slides: value.doc?.slides,
                slideID,
              });
              if (!slide) return;
              const incomingArtboards = hydrateArtboards({
                artboards: slide.artboards || {},
                nodes: {},
                allowSharedNodes: false,
              });
              const localSlide = getSlideByID({
                slides: slidesRef.current,
                slideID,
              });
              const nextArtboards = {
                ...(localSlide?.artboards || {}),
                ...incomingArtboards,
              };
              const nextSlides = alignSlideFormats({
                slides: patchSlideArtboards({
                  slides: slidesRef.current.length
                    ? slidesRef.current
                    : value.doc?.slides || [],
                  slideID,
                  artboards: nextArtboards,
                }),
                artboards: nextArtboards,
              });
              const alignedArtboards =
                getSlideByID({ slides: nextSlides, slideID })?.artboards ||
                nextArtboards;
              slidesRef.current = nextSlides;
              setSlides(nextSlides);
              if (slideID !== activeSlideIDRef.current) return;
              const nextActiveID = resolveActiveArtboardID({
                artboards: alignedArtboards,
                activeArtboardID: activeArtboardIDRef.current,
              });
              const nextNodes = alignedArtboards[nextActiveID]?.nodes || {};
              const nextLayouts =
                alignedArtboards[nextActiveID]?.nodeLayouts || {};
              graphRef.current = { nodes: nextNodes, nodeLayouts: nextLayouts };
              artboardsRef.current = alignedArtboards;
              activeArtboardIDRef.current = nextActiveID;
              setArtboards(alignedArtboards);
              setActiveArtboardID(nextActiveID);
              setNodes(nextNodes);
            });
            const isolatedGuides = isolateCarouselSlideGuides({
              slides: slidesRef.current,
              assets: assetsRef.current,
            });
            if (isolatedGuides.changed) {
              slidesRef.current = isolatedGuides.slides;
              assetsRef.current = isolatedGuides.assets;
              setSlides(isolatedGuides.slides);
              setAssets(isolatedGuides.assets);
              const activeArtboards = getSlideByID({
                slides: isolatedGuides.slides,
                slideID: activeSlideIDRef.current,
              })?.artboards;
              if (activeArtboards) {
                artboardsRef.current = activeArtboards;
                setArtboards(activeArtboards);
                const activeID = resolveActiveArtboardID({
                  artboards: activeArtboards,
                  activeArtboardID: activeArtboardIDRef.current,
                });
                const activeNodes = activeArtboards[activeID]?.nodes || {};
                const activeLayouts =
                  activeArtboards[activeID]?.nodeLayouts || {};
                graphRef.current = {
                  nodes: activeNodes,
                  nodeLayouts: activeLayouts,
                };
                setNodes(activeNodes);
              }
            }
          } else {
            setAnalysis(value.doc?.editor?.analysis || null);
            if (carouselDoc) {
              setAnalysisBySlide(nextBySlide);
              analysisBySlideRef.current = nextBySlide;
            }
          }

          analysisStatusRef.current = nextStatus;
          analysisBySlideRef.current = nextBySlide;
          if (!hasLoadedRef.current) {
            hasLoadedRef.current = true;
            setIsLoading(false);
          }
        },
        onReferenceAvailable: (dbRef) => {
          listenerRef.current = dbRef;
        },
      })
    );

    return () => {
      dispatch(
        stopListenerCreativeTemplate({ listenerRef: listenerRef.current })
      );
    };
    // eslint-disable-next-line
  }, [dispatch, onBackToList, reset, templateID]);

  const selectedPath = useMemo(
    () => (selectedID ? findNodePath({ nodes, nodeID: selectedID }) : null),
    [nodes, selectedID]
  );
  const selectedNode = selectedPath?.node || null;
  const selectedLayout = useMemo(
    () =>
      selectedID
        ? getAbsolutePx({
            nodes,
            nodeLayouts,
            nodeID: selectedID,
            artboardWidth: artboard.width,
            artboardHeight: artboard.height,
          })
        : null,
    [artboard.height, artboard.width, nodeLayouts, nodes, selectedID]
  );

  useEffect(() => {
    const pendingInspectorReset = skipInspectorFormResetRef.current;
    if (
      pendingInspectorReset.skip &&
      pendingInspectorReset.nodeID === selectedID
    ) {
      skipInspectorFormResetRef.current = { skip: false, nodeID: null };
      return;
    }
    skipInspectorFormResetRef.current = { skip: false, nodeID: null };

    const templateName = getValues("name");
    if (!selectedNode) {
      reset({ ...getValues(), ...emptyInspector, name: templateName });
      return;
    }
    reset({
      ...getValues(),
      ...inspectorFromNode({ node: selectedNode, layout: selectedLayout }),
      ...inspectorOverlay(selectedNode),
      name: templateName,
    });
  }, [
    getValues,
    inspectorOverlay,
    reset,
    selectedID,
    selectedLayout,
    selectedNode,
  ]);

  const boxIdentityRef = useRef(new Map());
  const boxes = useMemo(() => {
    const nextBoxes = flattenTreeToBoxes({
      nodes,
      nodeLayouts,
      artboardWidth: artboard.width,
      artboardHeight: artboard.height,
    });
    // Any graph edit rebuilds every box, and a fresh object re-renders the
    // memoized canvas box even when nothing about that layer changed — which
    // means refitting every text layer on each tick of a slider drag. Layers
    // whose content is untouched keep their previous object so they can skip.
    const previous = boxIdentityRef.current;
    const nextIdentity = new Map();
    const list = nextBoxes.map((box) => {
      const resolved = {
        ...box,
        ...resolveImageLayerPreview({ box, assets }),
        ...resolveTextLayerPreview({ box }),
        inlineReplacementUrls: resolveInlineReplacementUrls({
          replacements: box.style?.inlineReplacements,
          assets,
        }),
      };
      const next =
        resolved.sourceKind === "generated"
          ? applySessionLayerTryPreview({
              box: resolved,
              preview:
                layerTryPreviews[
                  layerTryPreviewKey({
                    artboardID: activeArtboardID,
                    nodeID: box.id,
                  })
                ],
            })
          : resolved;
      const signature = JSON.stringify(next);
      const cached = previous.get(next.id);
      const stable = cached?.signature === signature ? cached.box : next;
      nextIdentity.set(next.id, { signature, box: stable });
      return stable;
    });
    boxIdentityRef.current = nextIdentity;
    return list;
  }, [
    activeArtboardID,
    artboard.height,
    artboard.width,
    assets,
    layerTryPreviews,
    nodeLayouts,
    nodes,
  ]);

  const nameSuggestions = useMemo(
    () =>
      listUncoveredNamesFromOtherArtboards({
        artboards,
        activeArtboardID,
      }),
    [activeArtboardID, artboards]
  );

  const tryingNodeIDs = useMemo(() => {
    const next = {};
    Object.keys(tryingLayerKeys).forEach((key) => {
      const { artboardID, nodeID } = parseLayerTryPreviewKey(key);
      if (artboardID === activeArtboardID && nodeID) {
        next[nodeID] = true;
      }
    });
    return next;
  }, [activeArtboardID, tryingLayerKeys]);

  const resizingBackgroundNodeIDs = useMemo(() => {
    const next = {};
    Object.keys(resizingBackgroundKeys).forEach((key) => {
      const { artboardID, nodeID } = parseLayerTryPreviewKey(key);
      if (artboardID === activeArtboardID && nodeID) {
        next[nodeID] = true;
      }
    });
    return next;
  }, [activeArtboardID, resizingBackgroundKeys]);

  const selectedTryKey = selectedID
    ? layerTryPreviewKey({
        artboardID: activeArtboardID,
        nodeID: selectedID,
      })
    : "";

  const selectedAssetUrl =
    selectedNode?.source?.kind === "asset" && selectedNode.source?.assetRef
      ? assets?.[selectedNode.source.assetRef]?.url || ""
      : "";
  const selectedOriginImageUrl =
    selectedNode?.type === "image" && selectedNode.originAssetRef
      ? assets?.[selectedNode.originAssetRef]?.url || ""
      : "";
  const selectedInlineReplacementUrls = resolveInlineReplacementUrls({
    replacements: selectedNode?.style?.inlineReplacements,
    assets,
  });

  const commitGraph = ({
    nodes: nextNodes,
    nodeLayouts: nextLayouts,
    artboards: nextArtboards,
  }) => {
    const activeID = activeArtboardIDRef.current;
    const baseArtboards = nextArtboards || artboardsRef.current;
    const resolvedLayouts =
      nextLayouts ?? baseArtboards[activeID]?.nodeLayouts ?? {};
    const resolvedArtboards = patchActiveArtboardGraph({
      artboards: baseArtboards,
      artboardID: activeID,
      nodes: nextNodes,
      nodeLayouts: resolvedLayouts,
    });
    const nextVariables = syncVariablesFromArtboards({
      artboards: resolvedArtboards,
      variables: variablesRef.current,
    });
    graphRef.current = { nodes: nextNodes, nodeLayouts: resolvedLayouts };
    artboardsRef.current = resolvedArtboards;
    if (
      isCarouselLayout({
        layoutKind: layoutKindRef.current,
        slides: slidesRef.current,
      })
    ) {
      const flushed = flushActiveCell({
        layoutKind: layoutKindRef.current,
        slides: slidesRef.current,
        artboards: resolvedArtboards,
        activeSlideID: activeSlideIDRef.current,
        activeArtboardID: activeID,
        nodes: nextNodes,
        nodeLayouts: resolvedLayouts,
      });
      slidesRef.current = flushed.slides;
      setSlides(flushed.slides);
    }
    variablesRef.current = nextVariables;
    setNodes(nextNodes);
    setArtboards(resolvedArtboards);
    setVariables(nextVariables);
  };

  const flushCurrentCell = () =>
    flushActiveCell({
      layoutKind: layoutKindRef.current,
      slides: slidesRef.current,
      artboards: artboardsRef.current,
      activeSlideID: activeSlideIDRef.current,
      activeArtboardID: activeArtboardIDRef.current,
      nodes: graphRef.current.nodes,
      nodeLayouts: graphRef.current.nodeLayouts,
    });

  const loadCell = ({ artboards: nextArtboards, slideID, artboardID }) => {
    const pinnedArtboards = hydrateArtboards({
      artboards: nextArtboards,
      nodes: {},
      allowSharedNodes: false,
    });
    const nextSlideID = slideID || activeSlideIDRef.current;
    const nextArtboardID = resolveActiveArtboardID({
      artboards: pinnedArtboards,
      activeArtboardID: artboardID,
    });
    const nextNodes = pinnedArtboards[nextArtboardID]?.nodes || {};
    const nextLayouts = pinnedArtboards[nextArtboardID]?.nodeLayouts || {};
    artboardsRef.current = pinnedArtboards;
    activeArtboardIDRef.current = nextArtboardID;
    activeSlideIDRef.current = nextSlideID;
    graphRef.current = { nodes: nextNodes, nodeLayouts: nextLayouts };
    setArtboards(pinnedArtboards);
    setActiveArtboardID(nextArtboardID);
    setActiveSlideID(nextSlideID);
    setNodes(nextNodes);
    setEditingTextID(null);
    setTextSelection(null);
    const nextSelectedIDs = selectedIDsRef.current.filter((id) =>
      findNodePath({ nodes: nextNodes, nodeID: id })
    );
    applySelection(
      nextSelectedIDs.includes(selectedIDRef.current)
        ? selectedIDRef.current
        : nextSelectedIDs[nextSelectedIDs.length - 1] || null,
      nextSelectedIDs
    );
  };

  persistHighlightPruneRef.current = ({ nodeID, highlights }) => {
    const found = findNodePath({
      nodes: graphRef.current.nodes,
      nodeID,
    });
    if (!found) return;
    const style = { ...(found.node.style || {}) };
    if (Array.isArray(highlights) && highlights.length) {
      style.highlights = highlights;
    } else {
      delete style.highlights;
    }
    commitGraph({
      nodeLayouts: graphRef.current.nodeLayouts,
      nodes: patchNodeById({
        nodes: graphRef.current.nodes,
        nodeID,
        patch: { style },
      }),
    });
  };

  // The style patch a text edit implies for the layer highlights: phrases that
  // were touched by the edit are re-read from the new text so they keep
  // painting, and the standing selection follows the rename.
  const highlightsPatchForTextEdit = ({ node, nextText }) => {
    const current = node?.style?.highlights;
    if (!Array.isArray(current) || current.length === 0) return {};
    const { highlights, renamed } = retargetHighlightsForTextEdit({
      highlights: current,
      previousText: highlightSourceText(node),
      nextText,
    });
    if (areHighlightListsEqual(current, highlights)) return {};
    const renamedSelection = renamed[textSelectionRef.current?.phrase || ""];
    if (renamedSelection) {
      setTextSelection((selection) =>
        selection ? { ...selection, phrase: renamedSelection } : selection
      );
    }
    const style = { ...(node.style || {}) };
    if (highlights.length) style.highlights = highlights;
    else delete style.highlights;
    return { style };
  };

  const applySelection = (id, ids) => {
    const nextIDs = Array.isArray(ids) ? ids.filter(Boolean) : id ? [id] : [];
    const primary = id ?? nextIDs[nextIDs.length - 1] ?? null;
    selectedIDRef.current = primary;
    selectedIDsRef.current = nextIDs;
    setSelectedID(primary);
    setSelectedIDs(nextIDs);
  };

  const onSelect = (id, event, options = {}) => {
    blurActiveEditable(event);
    if (editingTextID && editingTextID !== id) {
      setEditingTextID(null);
    }
    const next = nextLayerSelection({
      currentIDs: selectedIDsRef.current,
      id,
      event,
      preserveMulti: options.preserveMulti,
    });
    applySelection(next.primary, next.ids);
  };

  const onSelectIds = (ids, event) => {
    blurActiveEditable(event);
    const next = nextMarqueeSelection({
      currentIDs: selectedIDsRef.current,
      ids,
      event,
    });
    applySelection(next.primary, next.ids);
  };

  const onToggleLock = (id, locked) => {
    const found = findNodePath({
      nodes: graphRef.current.nodes,
      nodeID: id,
    });
    if (!found) return;
    push();
    commitGraph({
      nodes: patchNodeById({
        nodes: graphRef.current.nodes,
        nodeID: id,
        patch: { locked: Boolean(locked) },
      }),
    });
  };

  const onToggleVisible = (id, visible) => {
    const found = findNodePath({
      nodes: graphRef.current.nodes,
      nodeID: id,
    });
    if (!found) return;
    push();
    commitGraph(
      setNodeVisible({
        nodes: graphRef.current.nodes,
        nodeLayouts: graphRef.current.nodeLayouts,
        nodeID: id,
        visible: Boolean(visible),
      })
    );
  };

  const onUpdateBoxes = (updates = []) => {
    if (!updates.length) return;
    const { nodes: currentNodes, nodeLayouts: currentLayouts } =
      graphRef.current;
    const result = applyOverlayBoxes({
      nodes: currentNodes,
      nodeLayouts: currentLayouts,
      updates,
      artboardWidth: artboard.width,
      artboardHeight: artboard.height,
    });
    const ids = updates.map((item) => item.id).sort();
    push({ coalesceKey: `transform:${ids.join(",")}` });
    commitGraph(result);
  };

  const onUpdateBox = ({ id, nx, ny, nw, nh, rotation, crop }) => {
    onUpdateBoxes([{ id, nx, ny, nw, nh, rotation, crop }]);
  };

  const onAlignLayers = (align) => {
    const ids = selectedIDsRef.current.filter(Boolean);
    if (ids.length < 2) return;
    const selectedBoxes = boxes
      .filter((box) => ids.includes(box.id) && !box.isBackground && !box.locked)
      .map((box) => ({
        id: box.id,
        x: box.nx * artboard.width,
        y: box.ny * artboard.height,
        width: box.nw * artboard.width,
        height: box.nh * artboard.height,
      }));
    if (selectedBoxes.length < 2) return;
    const aligned = alignSelectedBoxes({ boxes: selectedBoxes, align });
    onUpdateBoxes(
      aligned.map((box) => ({
        id: box.id,
        nx: box.x / artboard.width,
        ny: box.y / artboard.height,
        nw: box.width / artboard.width,
        nh: box.height / artboard.height,
      }))
    );
  };

  const onCreateBox = ({ nx, ny, nw, nh, type = "text" }) => {
    const { nodes: currentNodes, nodeLayouts: currentLayouts } =
      graphRef.current;
    const parentID = resolveInsertParentID({
      nodes: currentNodes,
      selectedID: selectedIDRef.current,
    });
    const id = dispatch(getKeyDatabase());
    const names = {
      text: t(TEMPLATES_PRODUCE_ADD_TEXT),
      image: t(TEMPLATES_PRODUCE_ADD_IMAGE),
    };
    const node = createDefaultNode({
      id,
      name: names[type] || t(TEMPLATES_NEW_LAYER),
      existingNames: listNodeNames({ nodes: currentNodes }),
      type,
    });
    if (type === "text") {
      const defaultStyle = defaultTextStyleFromCatalog({
        families: fontFamiliesRef.current || [],
        brandFontFamily: brandFontFamilyRef.current || "",
        existingFontAssetRefs: collectTextFontAssetRefsFromNodes({
          nodes: currentNodes,
        }),
      });
      if (defaultStyle.fontAssetRef) {
        node.style = { ...(node.style || {}), ...defaultStyle };
      }
    }
    const result = insertNodeAtOverlay({
      nodes: currentNodes,
      nodeLayouts: currentLayouts,
      parentID,
      node,
      nx,
      ny,
      nw,
      nh,
      artboardWidth: artboard.width,
      artboardHeight: artboard.height,
    });
    if (!result.ok) return;
    push();
    commitGraph(result);
    applySelection(id, [id]);
    if (parentID) {
      setExpandedIDs((current) => ({ ...current, [parentID]: true }));
    }
  };

  const onAddImageLayersFromUrls = async (urls = []) => {
    const validUrls = (Array.isArray(urls) ? urls : [urls]).filter(Boolean);
    if (!validUrls.length) return;

    const activeID = activeArtboardIDRef.current;
    const currentArtboard = artboardsRef.current[activeID] || {};
    const artboardWidth = currentArtboard.width || artboard.width || 1;
    const artboardHeight = currentArtboard.height || artboard.height || 1;
    const prepared = [];
    for (const url of validUrls) {
      const dimensions = await readImageDimensionsFromUrl({ url });
      prepared.push({ url, ...dimensions });
    }

    let { nodes: nextNodes, nodeLayouts: nextLayouts } = graphRef.current;
    let nextAssets = { ...assetsRef.current };
    const parentID = resolveInsertParentID({
      nodes: nextNodes,
      selectedID: selectedIDRef.current,
    });
    const existingNames = listNodeNames({ nodes: nextNodes });
    let lastID = null;
    let insertedCount = 0;

    prepared.forEach(({ url, width: imageWidth, height: imageHeight }) => {
      const id = dispatch(getKeyDatabase());
      const node = createDefaultNode({
        id,
        name: t(TEMPLATES_PRODUCE_ADD_IMAGE),
        existingNames,
        type: "image",
      });
      existingNames.push(node.name);
      const assetKey = nextAssetKey({
        name: node.name,
        assets: nextAssets,
        artboards: artboardsRef.current,
        slides: slidesRef.current,
      });
      nextAssets = {
        ...nextAssets,
        [assetKey]: {
          type: "image",
          assetID: dispatch(getKeyDatabase()),
          mimeType: imageMimeFromUrl(url),
          url,
        },
      };
      node.source = { kind: "asset", assetRef: assetKey };
      Object.assign(node, imageOriginPatch({ node, assetRef: assetKey }));
      const offset = 0.04 * insertedCount;
      const { nw, nh } = fitImageOverlayBox({
        imageWidth,
        imageHeight,
        canvasWidth: artboardWidth,
        canvasHeight: artboardHeight,
      });
      const result = insertNodeAtOverlay({
        nodes: nextNodes,
        nodeLayouts: nextLayouts,
        parentID,
        node,
        nx: 0.12 + offset,
        ny: 0.12 + offset,
        nw,
        nh,
        artboardWidth,
        artboardHeight,
      });
      if (!result.ok) return;
      nextNodes = result.nodes;
      nextLayouts = result.nodeLayouts;
      lastID = id;
      insertedCount += 1;
    });

    if (!insertedCount) return;

    push();
    assetsRef.current = nextAssets;
    setAssets(nextAssets);
    commitGraph({ nodes: nextNodes, nodeLayouts: nextLayouts });
    if (lastID) {
      applySelection(lastID, [lastID]);
    }
    if (parentID) {
      setExpandedIDs((current) => ({ ...current, [parentID]: true }));
    }
  };

  const onOpenAssetsGallery = (mode) => {
    if (isLocked) return;
    setAssetsGalleryMode(mode);
    setIsAssetsGalleryOpen(true);
  };

  const onAddLayer = ({ type } = {}) => {
    const layerType = type || "text";
    switch (layerType) {
      case "image":
        onOpenAssetsGallery(ASSETS_GALLERY_MODE.ADD);
        return;
      case "text":
        onCreateBox({ ...defaultLayerBox(layerType), type: layerType });
        return;
      default: {
        const exhaustive = layerType;
        throw new Error(`Unhandled add layer type: ${exhaustive}`);
      }
    }
  };

  const copySelectedLayer = (nodeID) => {
    if (isLockedRef.current) return false;
    const activeID = activeArtboardIDRef.current;
    const currentArtboard = artboardsRef.current[activeID] || {};
    const { ids: nodeIDs } = resolveOperationIds({
      explicitID: nodeID,
      selectedIDs: selectedIDsRef.current,
      fallbackID: selectedIDRef.current,
    });
    const payload = copyNodesByIds({
      nodes: graphRef.current.nodes,
      nodeLayouts: graphRef.current.nodeLayouts,
      assets: assetsRef.current,
      nodeIDs,
      artboardWidth: currentArtboard.width || artboard.width || 1,
      artboardHeight: currentArtboard.height || artboard.height || 1,
    });
    if (!payload) return false;
    setCanvasClipboard(payload, { persist: true });
    return true;
  };

  const pasteCopiedLayer = () => {
    if (isLockedRef.current) return false;
    const payload = getCanvasClipboard({ persist: true });
    if (payload?.kind !== CANVAS_CLIPBOARD_KIND.TEMPLATE_NODE) return false;
    const activeID = activeArtboardIDRef.current;
    const currentArtboard = artboardsRef.current[activeID] || {};
    const pasteCount = consumeClipboardPasteCount({ persist: true });
    const result = pasteCopiedNodes({
      nodes: graphRef.current.nodes,
      nodeLayouts: graphRef.current.nodeLayouts,
      assets: assetsRef.current,
      payload,
      selectedID: selectedIDRef.current,
      createID: () => dispatch(getKeyDatabase()),
      artboardWidth: currentArtboard.width || artboard.width || 1,
      artboardHeight: currentArtboard.height || artboard.height || 1,
      pasteCount,
    });
    if (!result.ok) return false;
    push();
    assetsRef.current = result.assets;
    setAssets(result.assets);
    commitGraph({ nodes: result.nodes, nodeLayouts: result.nodeLayouts });
    if (result.pastedID) {
      applySelection(result.pastedID, result.pastedIDs || [result.pastedID]);
      const parentID = findNodePath({
        nodes: result.nodes,
        nodeID: result.pastedID,
      })?.parentID;
      if (parentID) {
        setExpandedIDs((current) => ({ ...current, [parentID]: true }));
      }
    }
    return true;
  };

  const deleteSelectedLayer = (nodeID) => {
    if (isLockedRef.current) return false;
    const { ids: requested } = resolveOperationIds({
      explicitID: nodeID,
      selectedIDs: selectedIDsRef.current,
      fallbackID: selectedIDRef.current,
    });
    const ids = requested.filter((id) => {
      const found = findNodePath({ nodes: graphRef.current.nodes, nodeID: id });
      return (
        found &&
        !isCanvasBackgroundNode({ node: found.node, parentID: found.parentID })
      );
    });
    if (ids.length === 0) return false;
    push();
    commitGraph(
      deleteNodesByIds({
        nodes: graphRef.current.nodes,
        nodeLayouts: graphRef.current.nodeLayouts,
        nodeIDs: ids,
      })
    );
    const removed = new Set(ids);
    if (selectedIDsRef.current.some((item) => removed.has(item))) {
      const next = selectedIDsRef.current.filter((item) => !removed.has(item));
      applySelection(next[next.length - 1] || null, next);
    }
    if (editingTextID && removed.has(editingTextID)) {
      setEditingTextID(null);
    }
    return true;
  };
  copyPasteHandlersRef.current = {
    copy: copySelectedLayer,
    paste: pasteCopiedLayer,
    delete: deleteSelectedLayer,
  };

  const onAddFullCanvasLayer = ({
    skipHistory = false,
    asBackground = false,
  } = {}) => {
    const { nodes: currentNodes, nodeLayouts: currentLayouts } =
      graphRef.current;
    const activeArtboard = artboardsRef.current[activeArtboardIDRef.current];
    const id = dispatch(getKeyDatabase());
    const node = applySourceKind({
      node: createDefaultNode({
        id,
        name: t(TEMPLATES_BACKGROUND_LAYER),
        existingNames: listNodeNames({ nodes: currentNodes }),
        type: "image",
      }),
      kind: "generated",
    });
    const result = insertNodeAtOverlay({
      nodes: currentNodes,
      nodeLayouts: currentLayouts,
      parentID: null,
      node,
      nx: 0,
      ny: 0,
      nw: 1,
      nh: 1,
      artboardWidth: activeArtboard?.width || artboard.width,
      artboardHeight: activeArtboard?.height || artboard.height,
    });
    if (!result.ok) return;
    if (!skipHistory) push();
    commitGraph(
      asBackground
        ? {
            ...result,
            nodes: setRootCanvasBackground({
              nodes: result.nodes,
              nodeID: id,
              isBackground: true,
            }),
          }
        : result
    );
    applySelection(id, [id]);
  };

  const showImageTooSmallAlert = () => {
    SimpleAlert({
      title: t(WARNING),
      text: t(TEMPLATES_ANALYZE_IMAGE_TOO_SMALL),
      icon: "error",
    });
  };

  const onAnalyzeCreative = async ({ file } = {}) => {
    if (isLocked && !isAnalysisError) return;

    if (file) {
      try {
        const dimensions = await getImageDimensionsFromFile({ file });
        if (isLayerizeImageTooSmall(dimensions)) {
          showImageTooSmallAlert();
          return;
        }
      } catch {
        SimpleAlert({
          title: t(TEMPLATES_UPLOAD_ERROR),
          icon: "error",
        });
        return;
      }
    } else if (
      isLayerizeImageTooSmall({
        width: artboard.width,
        height: artboard.height,
      })
    ) {
      showImageTooSmallAlert();
      return;
    }

    const confirmation = await confirmAnalyzeDetectsText({ t });
    if (!confirmation.proceed) return;

    const isBlocked = await dispatch(
      getIsBlockedActions({
        action: ADS_CAMPAIGN_ANALYZE_IMAGE_LAYERS_ACTION,
        showUsageConfirmationAlert: true,
        multiplier: 1,
      })
    );
    if (isBlocked) return;

    const currentName = (getValues("name") || "").trim();
    if (!currentName) {
      setValue("name", t(TEMPLATES_UNTITLED), {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
    const saved = await onSave({ silent: true });
    if (!saved?.ok) {
      return;
    }

    const isCarouselUpload = isCarouselLayout({
      layoutKind: layoutKindRef.current,
      slides: slidesRef.current,
    });
    const uploadKey = isCarouselUpload ? activeSlideIDRef.current || "" : "";
    if (file) {
      setUploadingSlideIDs((current) =>
        current.includes(uploadKey) ? current : [...current, uploadKey]
      );
      setIsGuideVisible(false);
    }
    const response = await dispatch(
      startAttachOriginCreativeAndAnalyze({
        templateID,
        file,
        artboardID: activeArtboardIDRef.current,
        slideID: isCarouselUpload ? activeSlideIDRef.current : undefined,
        extractText: confirmation.extractText,
      })
    );
    if (file) {
      setUploadingSlideIDs((current) =>
        current.filter((id) => id !== uploadKey)
      );
    }
    if (!response.ok && response.reason !== "analyzeFailed") {
      if (response.reason === "imageTooSmall") {
        showImageTooSmallAlert();
        return;
      }
      SimpleAlert({
        title: t(
          response.reason === "unauthorized"
            ? TEMPLATES_UNAUTHORIZED
            : TEMPLATES_UPLOAD_ERROR
        ),
        icon: "error",
      });
    }
  };

  const onUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    await onAnalyzeCreative({ file });
  };

  const onUploadReference = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || isLocked) return;

    const carouselUpload = isCarouselLayout({
      layoutKind: layoutKindRef.current,
      slides: slidesRef.current,
    });
    const targetSlideID = carouselUpload ? activeSlideIDRef.current : "";
    const targetArtboardID = activeArtboardIDRef.current;
    const uploadKey = carouselUpload ? targetSlideID : "__single__";

    let workingArtboards = artboardsRef.current;
    if (carouselUpload && targetSlideID) {
      if (activeSlideIDRef.current === targetSlideID) {
        workingArtboards = patchActiveArtboardGraph({
          artboards: artboardsRef.current,
          artboardID: targetArtboardID,
          nodes: graphRef.current.nodes,
          nodeLayouts: graphRef.current.nodeLayouts,
        });
        artboardsRef.current = workingArtboards;
        const flushedSlides = patchSlideArtboards({
          slides: slidesRef.current,
          slideID: targetSlideID,
          artboards: workingArtboards,
        });
        slidesRef.current = flushedSlides;
        setSlides(flushedSlides);
      } else {
        workingArtboards =
          getSlideByID({ slides: slidesRef.current, slideID: targetSlideID })
            ?.artboards || artboardsRef.current;
      }
    } else {
      workingArtboards = patchActiveArtboardGraph({
        artboards: artboardsRef.current,
        artboardID: targetArtboardID,
        nodes: graphRef.current.nodes,
        nodeLayouts: graphRef.current.nodeLayouts,
      });
      artboardsRef.current = workingArtboards;
    }

    const currentArtboard = workingArtboards[targetArtboardID];
    if (!currentArtboard) return;

    setUploadingGuideSlideIDs((current) =>
      current.includes(uploadKey) ? current : [...current, uploadKey]
    );

    try {
      const response = await dispatch(
        startAttachArtboardReference({
          templateID,
          artboardID: targetArtboardID,
          file,
          artboard: currentArtboard,
          artboards: workingArtboards,
          slideID: carouselUpload ? targetSlideID : undefined,
        })
      );
      if (!response.ok) {
        SimpleAlert({
          title: t(
            response.reason === "unauthorized"
              ? TEMPLATES_UNAUTHORIZED
              : TEMPLATES_UPLOAD_ERROR
          ),
          icon: "error",
        });
        return;
      }

      const canonicalAssetKey =
        carouselUpload && targetSlideID
          ? getReferenceAssetKey({
              artboardID: targetArtboardID,
              slideID: targetSlideID,
            })
          : response.data.assetKey;
      const uploadedAsset = response.data.asset;

      if (carouselUpload) {
        setSlides((prevSlides) => {
          const mergedAssets = {
            ...assetsRef.current,
            [canonicalAssetKey]: uploadedAsset,
          };
          const isolated = isolateCarouselSlideGuides({
            slides: prevSlides,
            assets: mergedAssets,
          });

          const slideArtboards =
            getSlideByID({
              slides: isolated.slides,
              slideID: targetSlideID,
            })?.artboards || {};
          const nextArtboards = {
            ...slideArtboards,
            [targetArtboardID]: {
              ...(slideArtboards[targetArtboardID] || {}),
              referenceAssetRef: canonicalAssetKey,
            },
          };
          const nextSlides = patchSlideArtboards({
            slides: isolated.slides,
            slideID: targetSlideID,
            artboards: nextArtboards,
          });

          assetsRef.current = mergedAssets;
          slidesRef.current = nextSlides;
          return nextSlides;
        });
        setAssets((prevAssets) => {
          const nextAssets = {
            ...prevAssets,
            [canonicalAssetKey]: uploadedAsset,
          };
          assetsRef.current = nextAssets;
          return nextAssets;
        });
        if (activeSlideIDRef.current === targetSlideID) {
          const activeArtboards =
            getSlideByID({
              slides: slidesRef.current,
              slideID: targetSlideID,
            })?.artboards || null;
          if (activeArtboards) {
            artboardsRef.current = activeArtboards;
            setArtboards(activeArtboards);
            setGuideOpacity((current) => (current > 0 ? current : 0.3));
            setIsGuideVisible(true);
          }
        }
        return;
      }

      const nextAssets = {
        ...assetsRef.current,
        [canonicalAssetKey]: uploadedAsset,
      };
      const nextArtboards = {
        ...artboardsRef.current,
        [targetArtboardID]: {
          ...(artboardsRef.current[targetArtboardID] || {}),
          referenceAssetRef: canonicalAssetKey,
        },
      };
      assetsRef.current = nextAssets;
      artboardsRef.current = nextArtboards;
      setAssets(nextAssets);
      setArtboards(nextArtboards);
      setGuideOpacity((current) => (current > 0 ? current : 0.3));
      setIsGuideVisible(true);
    } finally {
      setUploadingGuideSlideIDs((current) =>
        current.filter((id) => id !== uploadKey)
      );
    }
  };

  const onSelectCreativeFromGallery = async (selectedItems) => {
    setIsCreativeGalleryOpen(false);
    const url = selectedItems?.[0]?.url;
    if (!url) return;
    try {
      const file = await blobUrlToFile(url);
      await onAnalyzeCreative({ file });
    } catch {
      SimpleAlert({
        title: t(TEMPLATES_UPLOAD_ERROR),
        icon: "error",
      });
    }
  };

  const onAssignLayerAssetFromUrl = async (url = "") => {
    if (isLocked || !url) return;
    const currentID = selectedIDRef.current;
    if (!currentID) return;

    const foundBefore = findNodePath({
      nodes: graphRef.current.nodes,
      nodeID: currentID,
    });
    if (!foundBefore) return;

    const activeID = activeArtboardIDRef.current;
    const currentArtboard = artboardsRef.current[activeID] || {};
    const artboardWidth = currentArtboard.width || artboard.width || 1;
    const artboardHeight = currentArtboard.height || artboard.height || 1;

    let overlay = null;
    try {
      const dimensions = await getImageDimensionsFromUrl({ url });
      if (dimensions.width > 0 && dimensions.height > 0) {
        const currentBox = getAbsolutePx({
          nodes: graphRef.current.nodes,
          nodeLayouts: graphRef.current.nodeLayouts,
          nodeID: currentID,
          artboardWidth,
          artboardHeight,
        }) || { x: 0, y: 0, width: 0, height: 0 };
        overlay = sizeOverlayBoxToNaturalImage({
          imageWidth: dimensions.width,
          imageHeight: dimensions.height,
          canvasWidth: artboardWidth,
          canvasHeight: artboardHeight,
          currentNx: currentBox.x / artboardWidth,
          currentNy: currentBox.y / artboardHeight,
          currentNw: currentBox.width / artboardWidth,
          currentNh: currentBox.height / artboardHeight,
        });
      }
    } catch {
      // Keep the previous box if the image cannot be measured.
    }

    const { nodes: currentNodes, nodeLayouts: currentLayouts } =
      graphRef.current;
    const found = findNodePath({ nodes: currentNodes, nodeID: currentID });
    if (!found) return;

    const assetKey = nextAssetKey({
      name: found.node.name,
      assets: assetsRef.current,
      artboards: artboardsRef.current,
      slides: slidesRef.current,
      existingRef: found.node.source?.assetRef,
    });
    const existingAsset = assetsRef.current[assetKey];
    push();
    const nextAssets = {
      ...assetsRef.current,
      [assetKey]: {
        type: "image",
        assetID: existingAsset?.assetID || dispatch(getKeyDatabase()),
        mimeType: imageMimeFromUrl(url),
        url,
      },
    };
    assetsRef.current = nextAssets;
    setAssets(nextAssets);
    const nextNodes = patchNodeById({
      nodes: currentNodes,
      nodeID: currentID,
      patch: {
        ...imageOriginPatch({ node: found.node, assetRef: assetKey }),
        source: { kind: "asset", assetRef: assetKey },
      },
    });
    const sized = overlay
      ? applyOverlayBox({
          nodes: nextNodes,
          nodeLayouts: currentLayouts,
          nodeID: currentID,
          nx: overlay.nx,
          ny: overlay.ny,
          nw: overlay.nw,
          nh: overlay.nh,
          crop: null,
          artboardWidth,
          artboardHeight,
        })
      : { nodes: nextNodes, nodeLayouts: currentLayouts };
    commitGraph({
      nodeLayouts: sized.nodeLayouts,
      nodes: sized.nodes,
    });
  };

  const onAssignInlineReplacementAssetFromUrl = (url = "") => {
    if (isLocked || !url) return;
    const currentID = selectedIDRef.current;
    const slotID = String(inlineReplacementSlotIDRef.current || "").trim();
    inlineReplacementSlotIDRef.current = null;
    if (!currentID || !slotID) return;

    const { nodes: currentNodes, nodeLayouts: currentLayouts } =
      graphRef.current;
    const found = findNodePath({ nodes: currentNodes, nodeID: currentID });
    if (!found || found.node?.type !== "text") return;

    const slots = listStoredInlineReplacements(
      found.node.style?.inlineReplacements
    );
    const slot = slots.find((item) => item.id === slotID);
    if (!slot) return;

    const assetKey = nextAssetKey({
      name: slot.phrase || found.node.name || "inline",
      assets: assetsRef.current,
      artboards: artboardsRef.current,
      existingRef: slot.assetRef,
    });
    const existingAsset = assetsRef.current[assetKey];
    push();
    const nextAssets = {
      ...assetsRef.current,
      [assetKey]: {
        type: "image",
        assetID: existingAsset?.assetID || dispatch(getKeyDatabase()),
        mimeType: imageMimeFromUrl(url),
        url,
      },
    };
    assetsRef.current = nextAssets;
    setAssets(nextAssets);
    const nextSlots = slots.map((item) =>
      item.id === slotID ? { ...item, assetRef: assetKey } : item
    );
    const style = {
      ...(found.node.style || {}),
      inlineReplacements: nextSlots,
    };
    commitGraph({
      nodeLayouts: currentLayouts,
      nodes: patchNodeById({
        nodes: currentNodes,
        nodeID: currentID,
        patch: { style },
      }),
    });
  };

  const onSelectAssetsGalleryItems = (selectedItems) => {
    const urls = (selectedItems || []).map((item) => item?.url).filter(Boolean);
    switch (assetsGalleryMode) {
      case ASSETS_GALLERY_MODE.ADD:
        onAddImageLayersFromUrls(urls);
        return;
      case ASSETS_GALLERY_MODE.REPLACE:
        onAssignLayerAssetFromUrl(urls[0]);
        return;
      case ASSETS_GALLERY_MODE.INLINE_REPLACEMENT:
        onAssignInlineReplacementAssetFromUrl(urls[0]);
        return;
      default: {
        const exhaustive = assetsGalleryMode;
        throw new Error(`Unhandled assets gallery mode: ${exhaustive}`);
      }
    }
  };

  const onCloseTryImageModal = () => {
    if (tryImageModalUrlRef.current) {
      URL.revokeObjectURL(tryImageModalUrlRef.current);
      tryImageModalUrlRef.current = "";
    }
    setTryImageModal(null);
  };

  const openTryImageModal = ({ imageBase64, prompt }) => {
    const modalUrl = base64PngToBlobUrl(imageBase64);
    if (!modalUrl) return;
    if (tryImageModalUrlRef.current) {
      URL.revokeObjectURL(tryImageModalUrlRef.current);
    }
    tryImageModalUrlRef.current = modalUrl;
    setTryImageModal({
      url: modalUrl,
      prompt: String(prompt || ""),
    });
  };

  const clearLayerTryPreview = (key) => {
    if (!key) return;
    setLayerTryPreviews((current) => {
      if (!current[key]) return current;
      const next = { ...current };
      revokeLayerTryPreview(next[key]);
      delete next[key];
      layerTryPreviewsRef.current = next;
      return next;
    });
  };

  const onTryGenerate = async () => {
    const node = selectedNode;
    if (!node || node.source?.kind !== "generated") return;
    const generator = node.type === "text" ? "text" : "image";
    if (generator === "image") {
      const isBlocked = await dispatch(
        getIsBlockedActions({
          action: ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
          showUsageConfirmationAlert: true,
          multiplier: 1,
        })
      );
      if (isBlocked) return;
    }

    const layout = getAbsolutePx({
      nodes: graphRef.current.nodes,
      nodeLayouts: graphRef.current.nodeLayouts,
      nodeID: node.id,
      artboardWidth: artboard.width,
      artboardHeight: artboard.height,
    });
    if (!layout?.width || !layout?.height) return;

    const previewKey = layerTryPreviewKey({
      artboardID: activeArtboardIDRef.current,
      nodeID: node.id,
    });
    if (tryingLayerKeysRef.current[previewKey]) return;
    setTryingLayerKeys((current) => {
      const next = { ...current, [previewKey]: true };
      tryingLayerKeysRef.current = next;
      return next;
    });
    try {
      const result = await dispatch(
        startPreviewCreativeTemplateLayer({
          templateID,
          node: pickPreviewLayerNode(node),
          box: {
            width: layout.width,
            height: layout.height,
          },
          assets: collectPreviewArtboardAssets({
            nodes: graphRef.current.nodes,
            assets: assetsRef.current,
          }),
          artboardID: activeArtboardIDRef.current,
          slideID: isCarouselLayout({
            layoutKind: layoutKindRef.current,
            slides: slidesRef.current,
          })
            ? activeSlideIDRef.current
            : undefined,
          sampleRow: collectPreviewSampleRow({
            nodes: graphRef.current.nodes,
            assets: assetsRef.current,
          }),
          platformLanguage: i18n.language,
        })
      );
      if (!result?.ok) {
        SimpleAlert({
          title: t(TEMPLATES_TRY_LAYER_ERROR),
          text: result?.message || t(TEMPLATES_TRY_LAYER_ERROR),
          icon: "error",
        });
        return;
      }

      const current = findNodePath({
        nodes: graphRef.current.nodes,
        nodeID: node.id,
      });
      const sentPrompt = String(node.source?.promptTemplate || "");
      const currentPrompt = String(current?.node?.source?.promptTemplate || "");
      if (
        current?.node?.source?.kind !== "generated" ||
        currentPrompt !== sentPrompt
      ) {
        return;
      }

      const resultGenerator = result.data?.generator;
      switch (resultGenerator) {
        case "text": {
          const text = String(result.data?.text || "").trim();
          if (!text) {
            SimpleAlert({
              title: t(TEMPLATES_TRY_LAYER_ERROR),
              icon: "error",
            });
            return;
          }
          setLayerTryPreviews((current) => {
            const next = { ...current };
            revokeLayerTryPreview(next[previewKey]);
            next[previewKey] = { type: "text", text };
            layerTryPreviewsRef.current = next;
            return next;
          });
          return;
        }
        case "image": {
          const blobUrl = base64PngToBlobUrl(result.data?.imageBase64);
          if (!blobUrl) {
            SimpleAlert({
              title: t(TEMPLATES_TRY_LAYER_ERROR),
              icon: "error",
            });
            return;
          }
          setLayerTryPreviews((current) => {
            const next = { ...current };
            revokeLayerTryPreview(next[previewKey]);
            next[previewKey] = { type: "image", blobUrl };
            layerTryPreviewsRef.current = next;
            return next;
          });
          openTryImageModal({
            imageBase64: result.data?.imageBase64,
            prompt: sentPrompt,
          });
          return;
        }
        default: {
          const exhaustive = resultGenerator;
          throw new Error(`Unhandled preview generator: ${exhaustive}`);
        }
      }
    } finally {
      setTryingLayerKeys((current) => {
        if (!current[previewKey]) return current;
        const next = { ...current };
        delete next[previewKey];
        tryingLayerKeysRef.current = next;
        return next;
      });
    }
  };

  const promptGenerationKey = ({ slideID, artboardID, nodeID }) =>
    `${slideID || ""}|${artboardID || ""}|${nodeID || ""}`;

  const setPromptGenerating = (key, isGenerating) => {
    setGeneratingPromptKeys((current) => {
      if (Boolean(current[key]) === isGenerating) return current;
      const next = { ...current };
      if (isGenerating) next[key] = true;
      else delete next[key];
      generatingPromptKeysRef.current = next;
      return next;
    });
  };

  const buildGeneratedPromptPatch = (node) => {
    if (!node || node.source?.kind !== "generated") return null;
    return (prompt) => {
      const nextSource = {
        ...(node.source || {}),
        kind: "generated",
        generator: node.type === "text" ? "text" : "image",
        promptTemplate: prompt,
      };
      delete nextSource.negativePrompt;
      return { source: nextSource };
    };
  };

  const applyGeneratedPrompt = ({ slideID, artboardID, nodeID, prompt }) => {
    const isCarousel = isCarouselLayout({
      layoutKind: layoutKindRef.current,
      slides: slidesRef.current,
    });
    const isActiveCell =
      artboardID === activeArtboardIDRef.current &&
      (!isCarousel || slideID === activeSlideIDRef.current);

    if (isActiveCell) {
      const { nodes: currentNodes, nodeLayouts: currentLayouts } =
        graphRef.current;
      const found = findNodePath({ nodes: currentNodes, nodeID });
      const makePatch = buildGeneratedPromptPatch(found?.node);
      if (!makePatch) return;
      clearLayerTryPreview(layerTryPreviewKey({ artboardID, nodeID }));
      push({ coalesceKey: `generatePrompt:${artboardID}:${nodeID}` });
      commitGraph({
        nodeLayouts: currentLayouts,
        nodes: patchNodeById({
          nodes: currentNodes,
          nodeID,
          patch: makePatch(prompt),
        }),
      });
      return;
    }

    const patchArtboards = (artboardsMap) => {
      const target = artboardsMap?.[artboardID];
      const found = findNodePath({ nodes: target?.nodes || {}, nodeID });
      const makePatch = buildGeneratedPromptPatch(found?.node);
      if (!makePatch) return null;
      return {
        ...artboardsMap,
        [artboardID]: {
          ...target,
          nodes: patchNodeById({
            nodes: target.nodes,
            nodeID,
            patch: makePatch(prompt),
          }),
        },
      };
    };

    if (!isCarousel || slideID === activeSlideIDRef.current) {
      const nextArtboards = patchArtboards(artboardsRef.current);
      if (!nextArtboards) return;
      push({ coalesceKey: `generatePrompt:${artboardID}:${nodeID}` });
      artboardsRef.current = nextArtboards;
      setArtboards(nextArtboards);
      if (isCarousel) {
        const nextSlides = patchSlideArtboards({
          slides: slidesRef.current,
          slideID,
          artboards: nextArtboards,
        });
        slidesRef.current = nextSlides;
        setSlides(nextSlides);
      }
      return;
    }

    let changed = false;
    const nextSlides = (slidesRef.current || []).map((slide) => {
      if (slide?.id !== slideID) return slide;
      const nextArtboards = patchArtboards(slide.artboards || {});
      if (!nextArtboards) return slide;
      changed = true;
      return { ...slide, artboards: nextArtboards };
    });
    if (!changed) return;
    push({ coalesceKey: `generatePrompt:${slideID}:${artboardID}:${nodeID}` });
    slidesRef.current = nextSlides;
    setSlides(nextSlides);
  };

  const onGenerateLayerPrompt = async ({
    nodeID,
    kind,
    originText,
    imageUrl,
  }) => {
    if (!nodeID) return;
    const isCarousel = isCarouselLayout({
      layoutKind: layoutKindRef.current,
      slides: slidesRef.current,
    });
    const target = {
      slideID: isCarousel ? activeSlideIDRef.current : "",
      artboardID: activeArtboardIDRef.current,
      nodeID,
    };
    const key = promptGenerationKey(target);
    if (generatingPromptKeysRef.current[key]) return;
    generatingPromptKeysRef.current = {
      ...generatingPromptKeysRef.current,
      [key]: true,
    };
    setPromptGenerating(key, true);
    try {
      const result = await dispatch(
        startGenerateCreativeTemplateLayerPrompt({
          kind,
          originText: kind === "text" ? originText : "",
          imageUrl: kind === "image" ? imageUrl : "",
          language: i18n.language,
        })
      );
      if (!result?.ok || !result.prompt) {
        SimpleAlert({
          title: t(TEMPLATES_GENERATE_PROMPT_ERROR),
          icon: "error",
        });
        return;
      }
      applyGeneratedPrompt({ ...target, prompt: result.prompt });
    } finally {
      setPromptGenerating(key, false);
    }
  };

  const selectedPromptGenerationKey = selectedID
    ? promptGenerationKey({
        slideID: isCarouselLayout({ layoutKind, slides }) ? activeSlideID : "",
        artboardID: activeArtboardID,
        nodeID: selectedID,
      })
    : "";

  const onChangeField = ({ key, value, nodeID }) => {
    const currentID = selectedIDRef.current;
    if (!currentID) return false;
    if (nodeID && nodeID !== currentID) return;
    if (key === "type" || key === "sourceKind" || key === "promptTemplate") {
      clearLayerTryPreview(
        layerTryPreviewKey({
          artboardID: activeArtboardIDRef.current,
          nodeID: currentID,
        })
      );
    }
    const { nodes: currentNodes, nodeLayouts: currentLayouts } =
      graphRef.current;
    const found = findNodePath({ nodes: currentNodes, nodeID: currentID });
    if (!found) return false;
    if (
      key === "promptTemplate" &&
      nodeID &&
      found.node.source?.kind !== "generated"
    ) {
      return;
    }
    push({ coalesceKey: `field:${currentID}:${key}` });
    const layout = getAbsolutePx({
      nodes: currentNodes,
      nodeLayouts: currentLayouts,
      nodeID: currentID,
      artboardWidth: artboard.width,
      artboardHeight: artboard.height,
    });

    if (key === "type") {
      commitGraph({
        nodeLayouts: currentLayouts,
        nodes: patchNodeById({
          nodes: currentNodes,
          nodeID: currentID,
          patch: applyNodeType({
            node: found.node,
            type: value,
            layout,
          }),
        }),
      });
      return;
    }
    if (key === "sourceKind") {
      const flushed = flushCurrentCell();
      const applyKind = (artboards) =>
        applySourceKindToArtboards({
          artboards,
          name: found.node.name,
          kind: value,
        });
      const isCarousel = isCarouselLayout({
        layoutKind: layoutKindRef.current,
        slides: flushed.slides,
      });
      const nextSlides = isCarousel
        ? mapSlideArtboards({
            slides: flushed.slides,
            mapper: applyKind,
          })
        : flushed.slides;
      const nextArtboards = isCarousel
        ? nextSlides.find((slide) => slide.id === activeSlideIDRef.current)
            ?.artboards || applyKind(flushed.artboards)
        : applyKind(flushed.artboards);
      slidesRef.current = nextSlides;
      setSlides(nextSlides);
      const active = nextArtboards[activeArtboardIDRef.current] || {};
      commitGraph({
        nodes: active.nodes || {},
        nodeLayouts: active.nodeLayouts || currentLayouts,
        artboards: nextArtboards,
      });
      return;
    }
    if (key === "isBackground") {
      const flushed = flushCurrentCell();
      const applyBackground = (artboards) =>
        applyCanvasBackgroundToArtboards({
          artboards,
          nodeID: currentID,
          isBackground: Boolean(value),
          clearCrop:
            resolveLayoutKind({
              layoutKind: layoutKindRef.current,
              slides: flushed.slides,
            }) === CREATIVE_TEMPLATE_LAYOUT_KIND.CONTINUOUS_CAROUSEL,
        });
      const isCarousel = isCarouselLayout({
        layoutKind: layoutKindRef.current,
        slides: flushed.slides,
      });
      const nextSlides = isCarousel
        ? mapSlideArtboards({
            slides: flushed.slides,
            mapper: applyBackground,
          })
        : flushed.slides;
      const nextArtboards = isCarousel
        ? nextSlides.find((slide) => slide.id === activeSlideIDRef.current)
            ?.artboards || applyBackground(flushed.artboards)
        : applyBackground(flushed.artboards);
      slidesRef.current = nextSlides;
      setSlides(nextSlides);
      const active = nextArtboards[activeArtboardIDRef.current] || {};
      commitGraph({
        nodes: active.nodes || {},
        nodeLayouts: active.nodeLayouts || {},
        artboards: nextArtboards,
      });
      return;
    }
    if (["posX", "posY", "posW", "posH"].includes(key)) {
      if (
        isCanvasBackgroundNode({
          node: found.node,
          parentID: found.parentID,
        })
      ) {
        commitGraph({
          nodes: currentNodes,
          nodeLayouts: applyCanvasBackgroundLayout({
            nodeLayouts: currentLayouts,
            nodeID: currentID,
            width: artboard.width,
            height: artboard.height,
          }),
        });
        return;
      }
      const next = {
        x: key === "posX" ? value : layout.x,
        y: key === "posY" ? value : layout.y,
        width: key === "posW" ? value : layout.width,
        height: key === "posH" ? value : layout.height,
      };
      commitGraph(
        applyOverlayBox({
          nodes: currentNodes,
          nodeLayouts: currentLayouts,
          nodeID: currentID,
          nx: next.x / artboard.width,
          ny: next.y / artboard.height,
          nw: next.width / artboard.width,
          nh: next.height / artboard.height,
          artboardWidth: artboard.width,
          artboardHeight: artboard.height,
        })
      );
      return;
    }

    let patch = {};
    if (key === "name") {
      patch = { name: value };
      if (found.node.source?.kind === "data") {
        patch.source = {
          ...found.node.source,
          kind: "data",
          variable: slugifyName({ name: value, fallback: found.node.id }),
        };
      }
    }
    if (key === "fixedValue") {
      patch = {
        ...textOriginPatch({ value }),
        source: { ...found.node.source, kind: "fixed", value },
      };
    }
    if (key === "originText") {
      patch = textOriginPatch({ value });
    }
    // Highlights are stored by phrase, so an edit inside a styled word has to
    // move the slot with it, or the paint vanishes as soon as the text stops
    // matching. A run deleted outright still drops.
    if (key === "fixedValue" || key === "originText") {
      patch = {
        ...patch,
        ...highlightsPatchForTextEdit({
          node: found.node,
          nextText: String(value ?? ""),
        }),
      };
    }
    if (key === "expression") {
      patch = {
        source: { ...found.node.source, kind: "computed", expression: value },
      };
    }
    if (key === "promptTemplate") {
      const nextSource = {
        ...(found.node.source || {}),
        kind: "generated",
        generator: found.node.type === "text" ? "text" : "image",
        promptTemplate: value,
      };
      delete nextSource.negativePrompt;
      patch = { source: nextSource };
    }
    if (key === "assetRef") {
      patch = {
        ...imageOriginPatch({ node: found.node, assetRef: value }),
        source: {
          ...found.node.source,
          kind: "asset",
          assetRef: value || null,
        },
      };
    }
    if (key === "fontSize") {
      const parsed = Number(value);
      patch = {
        style: {
          ...(found.node.style || {}),
          fontSize: Number.isFinite(parsed) && parsed > 0 ? parsed : undefined,
        },
      };
    }
    if (key === "lineHeight") {
      const parsed = Number(value);
      patch = {
        style: {
          ...(found.node.style || {}),
          lineHeight:
            Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined,
        },
      };
    }
    if (key === "letterSpacing") {
      const parsed = Number(value);
      patch = {
        style: {
          ...(found.node.style || {}),
          letterSpacing: Number.isFinite(parsed) ? parsed : undefined,
        },
      };
    }
    if (key === "strokeWidth") {
      const parsed = Number(value);
      patch = {
        style: {
          ...(found.node.style || {}),
          strokeWidth:
            Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined,
        },
      };
    }
    if (
      key === "shadowAngle" ||
      key === "shadowDistance" ||
      key === "shadowBlur"
    ) {
      const parsed = Number(value);
      patch = {
        style: {
          ...(found.node.style || {}),
          [key]: Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined,
        },
      };
    }
    if (key === "backgroundPadding" || key === "backgroundRadius") {
      const parsed = Number(value);
      patch = {
        style: {
          ...(found.node.style || {}),
          [key]: Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined,
        },
      };
    }
    if (key === "fontSelection") {
      const selection =
        value && typeof value === "object"
          ? {
              fontAssetRef: value.fontAssetRef,
              fontFaceRef: value.fontFaceRef,
              fontWeight: value.fontWeight,
            }
          : {};
      patch = { style: { ...(found.node.style || {}), ...selection } };
    }
    if (
      key === "fontAssetRef" ||
      key === "fontFaceRef" ||
      key === "fontWeight" ||
      key === "color" ||
      key === "strokeColor" ||
      key === "shadowColor" ||
      key === "backgroundColor" ||
      key === "align" ||
      key === "orientation" ||
      key === "textStyle"
    ) {
      patch = { style: { ...(found.node.style || {}), [key]: value } };
    }
    if (key === "maximumLines" || key === "minimumLines") {
      patch = {
        overflow: {
          ...(found.node.overflow || {}),
          strategy: "shrink",
          [key]: value,
        },
      };
    }
    if (key === "frameWidth" || key === "frameHeight") {
      patch = {
        componentFrame: {
          ...(found.node.componentFrame || {}),
          width:
            key === "frameWidth"
              ? value
              : found.node.componentFrame?.width || 200,
          height:
            key === "frameHeight"
              ? value
              : found.node.componentFrame?.height || 200,
        },
      };
    }
    const highlightStyle = styleForHighlightField({
      node: found.node,
      nodeID: currentID,
      key,
      value,
    });
    if (
      applyIfHighlightField(highlightStyle, (style) => {
        commitGraph({
          nodeLayouts: currentLayouts,
          nodes: patchNodeById({
            nodes: currentNodes,
            nodeID: currentID,
            patch: { style },
          }),
        });
      })
    ) {
      return;
    }

    if (key === "clipContent") patch = { clipContent: value };
    if (key === "locked") patch = { locked: Boolean(value) };
    if (key === "highlights") {
      const nextHighlights = Array.isArray(value) ? value : [];
      const style = { ...(found.node.style || {}) };
      if (nextHighlights.length) style.highlights = nextHighlights;
      else delete style.highlights;
      patch = { style };
    }
    if (key === "inlineReplacements") {
      const nextReplacements = Array.isArray(value) ? value : [];
      const style = { ...(found.node.style || {}) };
      if (nextReplacements.length) style.inlineReplacements = nextReplacements;
      else delete style.inlineReplacements;
      patch = { style };
    }

    commitGraph({
      nodeLayouts: currentLayouts,
      nodes: patchNodeById({
        nodes: currentNodes,
        nodeID: currentID,
        patch,
      }),
    });
    return true;
  };
  onChangeFieldRef.current = onChangeField;

  const onChangeFieldFromInspector = useCallback((args) => {
    const committed = onChangeFieldRef.current?.(args);
    if (!committed || !INSPECTOR_TEXT_FIELD_KEYS.has(args?.key)) return;
    // Typing already updated the field. Import and generate pass nodeID and
    // only patch the node, so the inspector must reset from that node.
    if (args?.nodeID) return;
    skipInspectorFormResetRef.current = {
      skip: true,
      nodeID: selectedIDRef.current,
    };
  }, []);

  // Handed to every memoized canvas box, so it has to keep its identity across
  // renders or the boxes re-render (and refit) on every edit anywhere.
  const onCommitFixedTextRef = useRef(null);
  const onCommitFixedTextStable = useCallback(
    (nodeID, value) => onCommitFixedTextRef.current?.(nodeID, value),
    []
  );
  const onCommitFixedText = (nodeID, value) => {
    applySelection(nodeID, [nodeID]);
    const found = findNodePath({
      nodes: graphRef.current.nodes,
      nodeID,
    });
    const sourceKind = found?.node?.source?.kind;
    onChangeField({
      key: isTextOriginPreviewKind(sourceKind) ? "originText" : "fixedValue",
      value,
    });
    const keepEditing = keepTextEditingRef.current;
    keepTextEditingRef.current = false;
    if (!keepEditing) setEditingTextID(null);
    endCoalesce();
  };
  onCommitFixedTextRef.current = onCommitFixedText;

  const onSelectArtboard = (artboardID) => {
    const flushed = flushCurrentCell();
    const alignedSlides = alignSlideFormats({
      slides: flushed.slides,
      artboards: flushed.artboards,
    });
    const slideID = resolveActiveSlideID({
      slides: alignedSlides,
      activeSlideID: activeSlideIDRef.current,
    });
    const nextArtboards =
      getSlideByID({ slides: alignedSlides, slideID })?.artboards ||
      flushed.artboards;
    if (!nextArtboards[artboardID]) return;
    slidesRef.current = alignedSlides;
    setSlides(alignedSlides);
    loadCell({
      artboards: nextArtboards,
      slideID,
      artboardID,
    });
  };

  const flushIfEditable = () => {
    if (
      isProcessingAnalysis({
        analysis: analysisBySlideRef.current[activeSlideIDRef.current],
      })
    ) {
      return {
        artboards: artboardsRef.current,
        slides: slidesRef.current,
      };
    }
    return flushCurrentCell();
  };

  const onSelectSlide = (slideID) => {
    if (
      !isCarouselLayout({
        layoutKind: layoutKindRef.current,
        slides: slidesRef.current,
      })
    ) {
      return;
    }
    const target = slidesRef.current.find((slide) => slide?.id === slideID);
    if (!target) return;
    const flushed = flushIfEditable();
    const currentFormatID = activeArtboardIDRef.current;
    const alignedSlides = alignSlideFormats({
      slides: flushed.slides,
      artboards: flushed.artboards,
    });
    slidesRef.current = alignedSlides;
    setSlides(alignedSlides);
    const nextSlide =
      alignedSlides.find((slide) => slide?.id === slideID) || target;
    loadCell({
      artboards: nextSlide.artboards || {},
      slideID,
      artboardID: currentFormatID,
    });
  };

  const onReorderSlides = ({ activeID, overID }) => {
    if (
      analyzingSlideIDs.includes(activeID) ||
      analyzingSlideIDs.includes(overID)
    ) {
      return;
    }
    const flushed = flushIfEditable();
    const nextSlides = reorderSlides({
      slides: flushed.slides,
      activeID,
      overID,
    });
    slidesRef.current = nextSlides;
    artboardsRef.current = flushed.artboards;
    setSlides(nextSlides);
    setArtboards(flushed.artboards);
  };

  const onDuplicateSlide = (slideID) => {
    if (analyzingSlideIDs.includes(slideID)) return;
    const flushed = flushIfEditable();
    const duplicatedSlides = duplicateSlide({
      slides: flushed.slides,
      slideID,
    });
    if (duplicatedSlides === flushed.slides) return;
    push();
    const isolatedGuides = isolateCarouselSlideGuides({
      slides: duplicatedSlides,
      assets: assetsRef.current,
    });
    const nextSlides = isolatedGuides.slides;
    if (isolatedGuides.assets !== assetsRef.current) {
      assetsRef.current = isolatedGuides.assets;
      setAssets(isolatedGuides.assets);
    }
    const sourceIndex = flushed.slides.findIndex(
      (slide) => slide?.id === slideID
    );
    const copy = nextSlides[sourceIndex + 1];
    slidesRef.current = nextSlides;
    setSlides(nextSlides);
    if (!copy) return;
    loadCell({
      artboards: copy.artboards || {},
      slideID: copy.id,
      artboardID: activeArtboardIDRef.current,
    });
  };

  const onDeleteSlide = (slideID) => {
    if (analyzingSlideIDs.includes(slideID)) return;
    const flushed = flushIfEditable();
    const nextSlides = removeSlide({
      slides: flushed.slides,
      slideID,
    });
    if (nextSlides === flushed.slides) return;
    push();
    const nextActiveSlideID = resolveActiveSlideID({
      slides: nextSlides,
      activeSlideID:
        slideID === activeSlideIDRef.current
          ? nextSlides[0]?.id
          : activeSlideIDRef.current,
    });
    const nextSlide =
      nextSlides.find((slide) => slide?.id === nextActiveSlideID) ||
      nextSlides[0];
    slidesRef.current = nextSlides;
    setSlides(nextSlides);
    loadCell({
      artboards: nextSlide?.artboards || {},
      slideID: nextActiveSlideID,
      artboardID: activeArtboardIDRef.current,
    });
  };

  const hasLayers =
    countAllLayoutNodes({
      layoutKind,
      slides,
      artboards,
      nodes,
    }) > 0;
  const canChangeLayoutKind = canWrite && !isLocked && !hasLayers;
  const layoutKindOptions = [
    {
      value: CREATIVE_TEMPLATE_LAYOUT_KIND.SINGLE,
      label: t(TEMPLATES_LAYOUT_KIND_IMAGE),
    },
    {
      value: CREATIVE_TEMPLATE_LAYOUT_KIND.CAROUSEL,
      label: t(TEMPLATES_LAYOUT_KIND_CAROUSEL),
    },
  ];
  const selectedLayoutKind =
    isCarousel || isContinuous
      ? CREATIVE_TEMPLATE_LAYOUT_KIND.CAROUSEL
      : CREATIVE_TEMPLATE_LAYOUT_KIND.SINGLE;
  const showCarouselModeSelector =
    CREATIVE_TEMPLATE_CONTINUOUS_CAROUSEL_ENABLED &&
    (isCarousel || isContinuous);
  const continuousModeSwitch = showCarouselModeSelector ? (
    <Tooltip
      title={
        <Box component="span" sx={{ whiteSpace: "pre-line" }}>
          {t(
            isContinuous
              ? TEMPLATES_LAYOUT_KIND_CONTINUOUS_TOOLTIP
              : TEMPLATES_LAYOUT_KIND_INDIVIDUAL_TOOLTIP
          )}
          {canChangeLayoutKind || hasLayers
            ? "\n\n" +
              t(
                canChangeLayoutKind
                  ? isContinuous
                    ? TEMPLATES_LAYOUT_KIND_TO_INDIVIDUAL_HINT
                    : TEMPLATES_LAYOUT_KIND_TO_CONTINUOUS_HINT
                  : isContinuous
                    ? TEMPLATES_LAYOUT_KIND_TO_INDIVIDUAL_BLOCKED
                    : TEMPLATES_LAYOUT_KIND_TO_CONTINUOUS_BLOCKED
              )
            : null}
        </Box>
      }
    >
      <span>
        <IconButton
          size="small"
          aria-label={t(
            isContinuous
              ? TEMPLATES_LAYOUT_KIND_CONTINUOUS
              : TEMPLATES_LAYOUT_KIND_INDIVIDUAL
          )}
          disabled={!canChangeLayoutKind}
          onClick={() =>
            onChangeCarouselMode(
              isContinuous
                ? CREATIVE_TEMPLATE_LAYOUT_KIND.CAROUSEL
                : CREATIVE_TEMPLATE_LAYOUT_KIND.CONTINUOUS_CAROUSEL
            )
          }
          sx={(muiTheme) => ({
            width: 32,
            height: 32,
            border: "1px solid #E4E4E7",
            borderRadius: BORDER_RADIUS_BUTTON,
            ...(isContinuous
              ? {
                  background: muiTheme.palette.button.primary.main,
                  color: "#fff",
                  borderColor: "transparent",
                  "& svg": { color: "#fff", stroke: "#fff" },
                  "&:hover": {
                    background: muiTheme.palette.button.primary.hover.main,
                    color: "#fff",
                  },
                }
              : { bgcolor: "#fff", color: "text.primary" }),
          })}
        >
          {isContinuous ? <PanelsLeftRight size={16} /> : <Square size={16} />}
        </IconButton>
      </span>
    </Tooltip>
  ) : null;
  const continuousSlideWidth = isContinuous
    ? getContinuousSlideWidth({
        width: artboards[activeArtboardID]?.width,
        slideCount: continuousSlideCount,
      })
    : null;
  const seamOffsets = continuousSlideWidth
    ? listSeamOffsets({
        slideWidth: continuousSlideWidth,
        slideCount: continuousSlideCount,
      })
    : undefined;

  const changeLayoutKind = (nextKind, { slideCount, stripFormat } = {}) => {
    if (!canChangeLayoutKind) return;
    const currentKind = resolveLayoutKind({
      layoutKind: layoutKindRef.current,
      slides: slidesRef.current,
    });
    if (nextKind === currentKind) return;
    const flushed = flushCurrentCell();
    const layerCount = countAllLayoutNodes({
      layoutKind: layoutKindRef.current,
      slides: flushed.slides,
      artboards: flushed.artboards,
      nodes: graphRef.current.nodes,
    });
    if (layerCount > 0) return;
    push();
    const next = convertToLayoutKind({
      nextLayoutKind: nextKind,
      artboards:
        currentKind === CREATIVE_TEMPLATE_LAYOUT_KIND.CONTINUOUS_CAROUSEL
          ? stripToSlideArtboards({
              artboards: flushed.artboards,
              slideCount: continuousSlideCountRef.current,
            })
          : flushed.artboards,
      activeArtboardID: activeArtboardIDRef.current,
      slideCount,
      stripFormat,
    });
    continuousSlideCountRef.current = getContinuousSlideCount({
      layoutConfigurations: next.layoutConfigurations,
    });
    setContinuousSlideCount(continuousSlideCountRef.current);
    const isolatedGuides =
      next.layoutKind === CREATIVE_TEMPLATE_LAYOUT_KIND.CAROUSEL
        ? isolateCarouselSlideGuides({
            slides: next.slides,
            assets: assetsRef.current,
          })
        : { slides: next.slides, assets: assetsRef.current };
    if (isolatedGuides.assets !== assetsRef.current) {
      assetsRef.current = isolatedGuides.assets;
      setAssets(isolatedGuides.assets);
    }
    const nextArtboards =
      next.layoutKind === CREATIVE_TEMPLATE_LAYOUT_KIND.CAROUSEL
        ? getSlideByID({
            slides: isolatedGuides.slides,
            slideID: next.activeSlideID,
          })?.artboards || next.artboards
        : next.artboards;
    layoutKindRef.current = next.layoutKind;
    slidesRef.current = isolatedGuides.slides;
    setLayoutKind(next.layoutKind);
    setSlides(isolatedGuides.slides);
    loadCell({
      artboards: nextArtboards,
      slideID: next.activeSlideID,
      artboardID: next.activeArtboardID,
    });
  };

  const onChangeLayoutKind = (nextKind) => {
    const currentKind = resolveLayoutKind({
      layoutKind: layoutKindRef.current,
      slides: slidesRef.current,
    });
    if (
      nextKind === CREATIVE_TEMPLATE_LAYOUT_KIND.CAROUSEL &&
      currentKind === CREATIVE_TEMPLATE_LAYOUT_KIND.CONTINUOUS_CAROUSEL
    ) {
      return;
    }
    changeLayoutKind(nextKind);
  };

  const onChangeCarouselMode = (nextKind) => {
    if (nextKind === CREATIVE_TEMPLATE_LAYOUT_KIND.CONTINUOUS_CAROUSEL) {
      setIsContinuousSetupOpen(true);
      return;
    }
    changeLayoutKind(nextKind);
  };

  const onConfirmContinuousSetup = ({ slideCount, format }) => {
    setIsContinuousSetupOpen(false);
    changeLayoutKind(CREATIVE_TEMPLATE_LAYOUT_KIND.CONTINUOUS_CAROUSEL, {
      slideCount,
      stripFormat: format,
    });
    if (
      layoutKindRef.current ===
      CREATIVE_TEMPLATE_LAYOUT_KIND.CONTINUOUS_CAROUSEL
    ) {
      onAddFullCanvasLayer({ skipHistory: true, asBackground: true });
    }
  };

  const onCenterStripSlide = (index) =>
    canvasApiRef.current?.centerOnSlide({ index });

  const applyStripArtboards = ({ artboards: nextArtboards, slideCount }) => {
    push();
    continuousSlideCountRef.current = slideCount;
    setContinuousSlideCount(slideCount);
    loadCell({
      artboards: nextArtboards,
      slideID: "",
      artboardID: activeArtboardIDRef.current,
    });
  };

  const onAddStripSlide = () => {
    if (!canWrite || isLocked) return;
    const flushed = flushCurrentCell();
    const result = addSlideToArtboards({
      artboards: flushed.artboards,
      slideCount: continuousSlideCountRef.current,
    });
    if (!result.changed) return;
    applyStripArtboards(result);
  };

  const onRemoveStripSlide = async (slideIndex) => {
    if (!canWrite || isLocked) return;
    const flushed = flushCurrentCell();
    const slideCount = continuousSlideCountRef.current;
    if (
      slideCount <= CREATIVE_TEMPLATE_CONTINUOUS_SLIDE_MIN ||
      slideIndex < 0 ||
      slideIndex >= slideCount
    ) {
      return;
    }
    if (
      hasContentInArtboardsSlideZone({
        artboards: flushed.artboards,
        slideIndex,
        slideCount,
      })
    ) {
      const confirmed = await ConfirmAlert({
        title: t(ARE_YOU_SURE),
        text: t(TEMPLATES_CONFIRM_DELETE_STRIP_SLIDE_DESCRIPTION, {
          index: slideIndex + 1,
        }),
        confirmButtonText: t(TEMPLATES_DELETE_SLIDE),
        cancelButtonText: t(TEMPLATES_KEEP_EDITING),
        confirmButtonVariant: "secondary",
        cancelButtonVariant: "secondary",
      });
      if (!confirmed?.isConfirmed) return;
    }
    const current = flushCurrentCell();
    const result = removeSlideFromArtboards({
      artboards: current.artboards,
      slideCount: continuousSlideCountRef.current,
      slideIndex,
    });
    if (!result.changed) return;
    applyStripArtboards(result);
  };

  const onAddSlide = () => {
    const flushed = flushIfEditable();
    const nextSlides = addSlide({
      slides: flushed.slides,
      artboards: flushed.artboards,
      empty: true,
    });
    if (nextSlides === flushed.slides) return;
    push();
    const added = nextSlides[nextSlides.length - 1];
    slidesRef.current = nextSlides;
    setSlides(nextSlides);
    loadCell({
      artboards: added?.artboards || {},
      slideID: added?.id,
      artboardID: activeArtboardIDRef.current,
    });
  };

  const onRequestAddFormat = async () => {
    if (!templateID || hasShownAddFormatHint(templateID)) {
      return true;
    }
    if (listArtboardIDs({ artboards: artboardsRef.current }).length > 1) {
      markAddFormatHintShown(templateID);
      return true;
    }
    markAddFormatHintShown(templateID);
    const confirmed = await ConfirmAlert({
      title: t(ARE_YOU_SURE),
      text: t(TEMPLATES_CONFIRM_ADD_FORMAT_DESCRIPTION),
      confirmButtonText: t(TEMPLATES_CONFIRM_ADD_FORMAT),
      cancelButtonText: t(TEMPLATES_KEEP_EDITING),
      confirmButtonVariant: "secondary",
      cancelButtonVariant: "secondary",
    });
    return Boolean(confirmed?.isConfirmed);
  };

  const onAddArtboardFormat = async ({ id, width, height, label }) => {
    if (!id || artboardsRef.current[id]) return;
    const flushed = flushCurrentCell();
    previousArtboardIDRef.current = activeArtboardIDRef.current;
    push();
    const empty = createEmptyArtboard({ width, height, label });
    const isCarousel = isCarouselLayout({
      layoutKind: layoutKindRef.current,
      slides: flushed.slides,
    });
    const nextSlides = isCarousel
      ? fanOutFormat({
          slides: flushed.slides,
          artboardID: id,
          artboard: empty,
        })
      : flushed.slides;
    const nextArtboards = isCarousel
      ? nextSlides.find((slide) => slide.id === activeSlideIDRef.current)
          ?.artboards || { ...flushed.artboards, [id]: empty }
      : { ...flushed.artboards, [id]: empty };
    slidesRef.current = nextSlides;
    setSlides(nextSlides);
    loadCell({
      artboards: nextArtboards,
      slideID: activeSlideIDRef.current,
      artboardID: id,
    });
    if (
      layoutKindRef.current ===
      CREATIVE_TEMPLATE_LAYOUT_KIND.CONTINUOUS_CAROUSEL
    ) {
      onCopyPreviousFormat();
    }
    await onSave({ silent: true });
  };

  const onAddArtboardPreset = (presetID) => {
    const preset = CREATIVE_TEMPLATE_ARTBOARD_PRESETS[presetID];
    if (!preset) return;
    if (!isContinuous) {
      onAddArtboardFormat(preset);
      return;
    }
    const strip = buildContinuousPresetArtboard({
      preset,
      slideCount: continuousSlideCountRef.current,
    });
    if (!strip) return;
    onAddArtboardFormat({
      id: preset.id,
      width: strip.width,
      height: strip.height,
      label: preset.label,
    });
  };

  const onAddCustomArtboard = ({ width, height }) => {
    const id = nextCustomArtboardID({
      width,
      height,
      artboards: isContinuous
        ? stripToSlideArtboards({
            artboards: artboardsRef.current,
            slideCount: continuousSlideCountRef.current,
          })
        : artboardsRef.current,
    });
    if (!isContinuous) {
      onAddArtboardFormat({
        id,
        width,
        height,
        label: `${width}×${height}`,
      });
      return;
    }
    const strip = buildContinuousPresetArtboard({
      preset: { width, height, label: `${width}×${height}` },
      slideCount: continuousSlideCountRef.current,
    });
    if (!strip) return;
    onAddArtboardFormat({
      id,
      width: strip.width,
      height: strip.height,
      label: strip.label,
    });
  };

  const replaceLayerReferenceAndFit = ({
    artboardID,
    slideID,
    nodeID,
    width,
    height,
    url,
  }) => {
    const carousel = isCarouselLayout({
      layoutKind: layoutKindRef.current,
      slides: slidesRef.current,
    });
    const sameSlide = !carousel || activeSlideIDRef.current === slideID;
    const isActive = sameSlide && activeArtboardIDRef.current === artboardID;
    const storedArtboards = carousel
      ? getSlideByID({ slides: slidesRef.current, slideID })?.artboards || {}
      : artboardsRef.current;
    const nodes = isActive
      ? graphRef.current.nodes
      : storedArtboards[artboardID]?.nodes || {};
    const nodeLayouts = isActive
      ? graphRef.current.nodeLayouts
      : storedArtboards[artboardID]?.nodeLayouts || {};
    const found = findNodePath({ nodes, nodeID });
    if (!found?.node || !url) return;
    const layerAssetKey = uniqueName({
      name: slugifyName({ name: found.node.name, fallback: "layer" }),
      existingNames: Object.keys(assetsRef.current || {}),
    });
    const layerAsset = {
      type: "image",
      assetID: dispatch(getKeyDatabase()),
      mimeType: imageMimeFromUrl(url),
      url,
      width,
      height,
    };
    setAssets((prevAssets) => {
      const nextAssets = {
        ...(prevAssets || {}),
        [layerAssetKey]: layerAsset,
      };
      assetsRef.current = nextAssets;
      return nextAssets;
    });
    const nextNodes = patchNodeById({
      nodes,
      nodeID,
      patch: imageOriginPatch({ assetRef: layerAssetKey }),
    });
    const result = applyOverlayBox({
      nodes: nextNodes,
      nodeLayouts,
      nodeID,
      nx: 0,
      ny: 0,
      nw: 1,
      nh: 1,
      artboardWidth: width,
      artboardHeight: height,
    });
    if (!result?.nodes || !result?.nodeLayouts) return;
    push();
    if (isActive) {
      commitGraph(result);
      return;
    }
    const nextArtboards = patchActiveArtboardGraph({
      artboards: storedArtboards,
      artboardID,
      nodes: result.nodes,
      nodeLayouts: result.nodeLayouts,
    });
    if (carousel) {
      const nextSlides = patchSlideArtboards({
        slides: slidesRef.current,
        slideID,
        artboards: nextArtboards,
      });
      slidesRef.current = nextSlides;
      setSlides(nextSlides);
      if (activeSlideIDRef.current === slideID) {
        artboardsRef.current = nextArtboards;
        setArtboards(nextArtboards);
      }
      return;
    }
    artboardsRef.current = nextArtboards;
    setArtboards(nextArtboards);
  };

  const offerUnmarkedBackgroundAiResize = async ({
    sourceArtboard,
    nodeID,
    width,
    height,
  }) => {
    const artboardID = activeArtboardIDRef.current;
    const slideID = activeSlideIDRef.current;
    const node = sourceArtboard?.nodes?.[nodeID];
    if (!node) return;
    const { assetUrl } = resolveImageLayerPreview({
      box: {
        type: node.type,
        sourceKind: node.source?.kind || "",
        assetRef: node.source?.assetRef || "",
        originAssetRef: node.originAssetRef || "",
      },
      assets: assetsRef.current,
    });
    if (!assetUrl) return;

    const confirmed = await ConfirmAlert({
      title: t(TEMPLATES_CONFIRM_AI_RESIZE_BACKGROUND),
      text: t(TEMPLATES_CONFIRM_AI_RESIZE_BACKGROUND_DESCRIPTION),
      confirmButtonText: t(TEMPLATES_AI_RESIZE_BACKGROUND_CONFIRM),
      cancelButtonText: t(TEMPLATES_KEEP_EDITING),
      confirmButtonVariant: "secondary",
      cancelButtonVariant: "secondary",
    });
    if (!confirmed?.isConfirmed) return;

    const isBlocked = await dispatch(
      getIsBlockedActions({
        action: ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
        multiplier: 1,
        showUsageConfirmationAlert: true,
      })
    );
    if (isBlocked) return;

    const resizeKey = layerTryPreviewKey({ artboardID, nodeID });
    setResizingBackgroundKeys((current) => ({
      ...current,
      [resizeKey]: true,
    }));
    try {
      const response = await dispatch(
        startResizeImage({
          ...buildResizeImagePayload({
            urls: [assetUrl],
            format: MEDIA_DIMENSIONS.custom,
            width,
            height,
          }),
          hideActivity: true,
        })
      );
      const resizedUrl = response?.ok
        ? extractResizeResultUrls(response)[0]
        : "";
      if (!resizedUrl) {
        if (response?.ok !== false) {
          SimpleAlert({
            title: t(TEMPLATES_UPLOAD_ERROR),
            icon: "error",
          });
        }
        return;
      }

      replaceLayerReferenceAndFit({
        artboardID,
        slideID,
        nodeID,
        width,
        height,
        url: resizedUrl,
      });
    } catch {
      SimpleAlert({
        title: t(TEMPLATES_UPLOAD_ERROR),
        icon: "error",
      });
    } finally {
      setResizingBackgroundKeys((current) => {
        if (!current[resizeKey]) return current;
        const next = { ...current };
        delete next[resizeKey];
        return next;
      });
    }
  };

  const onCopyPreviousFormat = () => {
    const currentID = activeArtboardIDRef.current;
    const current = artboardsRef.current[currentID];
    if (!current) return;
    const source = findCopySourceArtboard({
      artboards: artboardsRef.current,
      excludeID: currentID,
      preferredID: previousArtboardIDRef.current,
      width: current.width,
      height: current.height,
    });
    if (!source) return;
    push();
    const seeded = seedArtboard({
      fromArtboard: source,
      preset: {
        label: current.label,
        width: current.width,
        height: current.height,
      },
    });
    const nextArtboards = {
      ...artboardsRef.current,
      [currentID]: {
        ...current,
        nodes: seeded.nodes || {},
        nodeLayouts: seeded.nodeLayouts || {},
      },
    };
    artboardsRef.current = nextArtboards;
    graphRef.current = {
      nodes: seeded.nodes || {},
      nodeLayouts: seeded.nodeLayouts || {},
    };
    setArtboards(nextArtboards);
    setNodes(seeded.nodes || {});
    applySelection(null, []);
    const backgroundNodeID = inferImplicitCanvasBackgroundNodeID({
      nodes: source.nodes || {},
      nodeLayouts: source.nodeLayouts || {},
      width: source.width,
      height: source.height,
    });
    if (!backgroundNodeID) return;
    void offerUnmarkedBackgroundAiResize({
      sourceArtboard: source,
      nodeID: backgroundNodeID,
      width: current.width,
      height: current.height,
    });
  };

  const onRemoveActiveArtboard = async () => {
    const currentID = activeArtboardIDRef.current;
    const currentArtboards = artboardsRef.current;
    if (Object.keys(currentArtboards).length <= 1) return;
    const artboard = currentArtboards[currentID];
    if (!artboard) return;

    const formatLabel = getArtboardLabel({
      artboard,
      artboardID: currentID,
    });
    const confirmed = await ConfirmAlert({
      title: t(TEMPLATES_CONFIRM_REMOVE_FORMAT_TITLE),
      text: t(TEMPLATES_CONFIRM_REMOVE_FORMAT_DESCRIPTION, {
        format: formatLabel,
      }),
      confirmButtonText: t(TEMPLATES_REMOVE_FORMAT),
    });
    if (!confirmed.isConfirmed) return;

    push();
    const removed = currentArtboards[currentID];
    const nextWorking = { ...currentArtboards };
    delete nextWorking[currentID];
    const isCarousel = isCarouselLayout({
      layoutKind: layoutKindRef.current,
      slides: slidesRef.current,
    });
    const nextSlides = isCarousel
      ? removeFormatFromSlides({
          slides: slidesRef.current,
          artboardID: currentID,
        })
      : slidesRef.current;
    const nextActiveID = resolveActiveArtboardID({
      artboards: nextWorking,
      activeArtboardID: null,
    });
    const removedRefKeys = new Set(
      [
        removed?.referenceAssetRef,
        getReferenceAssetKey({
          artboardID: currentID,
          slideID: isCarousel ? activeSlideIDRef.current : undefined,
        }),
        ...(isCarousel
          ? (slidesRef.current || []).map(
              (slide) =>
                slide?.artboards?.[currentID]?.referenceAssetRef ||
                getReferenceAssetKey({
                  artboardID: currentID,
                  slideID: slide?.id,
                })
            )
          : []),
      ].filter(Boolean)
    );
    let nextAssets = assetsRef.current;
    const keysToDelete = [...removedRefKeys].filter(
      (key) => key !== "reference"
    );
    if (keysToDelete.length > 0) {
      nextAssets = { ...assetsRef.current };
      keysToDelete.forEach((key) => {
        delete nextAssets[key];
      });
      assetsRef.current = nextAssets;
      setAssets(nextAssets);
    }
    artboardsRef.current = nextWorking;
    slidesRef.current = nextSlides;
    setSlides(nextSlides);
    loadCell({
      artboards: nextWorking,
      slideID: activeSlideIDRef.current,
      artboardID: nextActiveID,
    });
  };

  useEffect(() => {
    setIsTemplateProducing(false);
    if (!templateID) return undefined;
    dispatch(
      startListenerTemplateActiveProduces({
        templateID,
        onUpdatedDataCallback: setIsTemplateProducing,
        onReferenceAvailable: (dbQuery) => {
          activeProducesListenerRef.current = dbQuery;
        },
      })
    );
    return () => {
      dispatch(
        stopListenerCreativeTemplateProduce({
          listenerRef: activeProducesListenerRef.current,
        })
      );
    };
  }, [dispatch, templateID]);

  const onSave = async ({ silent = false } = {}) => {
    if (isTemplateProducing) {
      if (!silent) {
        SimpleAlert({
          title: t(TEMPLATES_SAVE_ERROR),
          text: t(TEMPLATES_SAVE_BLOCKED_PRODUCING),
          icon: "warning",
        });
      }
      return { ok: false, reason: "producing" };
    }
    if (isLocked) {
      if (!silent) {
        SimpleAlert({
          title: t(TEMPLATES_UNAUTHORIZED),
          text: t(lockMessage),
          icon: "warning",
        });
      }
      return { ok: false, reason: "unauthorized" };
    }

    const trimmedName = (getValues("name") || "").trim();
    if (!silent) {
      const isNameValid = await trigger("name");
      if (!isNameValid || !trimmedName) {
        SimpleAlert({
          title: t(FIELD_NAME_REQUIRED),
          icon: "warning",
        });
        return { ok: false, reason: "nameRequired" };
      }
    }
    const nameToSave = trimmedName || t(TEMPLATES_UNTITLED);

    if (!silent) setIsSaving(true);
    const flushed = flushCurrentCell();
    const isCarousel = isCarouselLayout({
      layoutKind: layoutKindRef.current,
      slides: flushed.slides,
    });
    const alignedSlides = isCarousel
      ? alignSlideFormats({
          slides: flushed.slides,
          artboards: flushed.artboards,
        })
      : flushed.slides;
    const flushedArtboards = isCarousel
      ? getSlideByID({
          slides: alignedSlides,
          slideID: activeSlideIDRef.current,
        })?.artboards || flushed.artboards
      : flushed.artboards;
    artboardsRef.current = flushedArtboards;
    slidesRef.current = alignedSlides;
    setSlides(alignedSlides);
    setArtboards(flushedArtboards);
    const checkArtboards = isCarousel
      ? flattenCellsToArtboards({
          cells: listAllCells({
            layoutKind: layoutKindRef.current,
            slides: alignedSlides,
            artboards: flushedArtboards,
          }),
        })
      : flushedArtboards;
    if (!silent) {
      const mismatches = getLayoutVariableMismatches({
        layoutKind: layoutKindRef.current,
        slides: alignedSlides,
        artboards: flushedArtboards,
      });
      if (mismatches.length > 0) {
        const rows = mismatches.flatMap((mismatch) => {
          const slideArtboards = isCarousel
            ? getSlideByID({
                slides: alignedSlides,
                slideID: mismatch.slideID,
              })?.artboards || {}
            : flushedArtboards;
          const format = getArtboardLabel({
            artboard: slideArtboards[mismatch.artboardID],
            artboardID: mismatch.artboardID,
          });
          return mismatch.missing.map((item) => ({
            slide:
              mismatch.slideIndex >= 0
                ? t(TEMPLATES_SLIDE_LABEL, {
                    index: mismatch.slideIndex + 1,
                  })
                : "",
            format,
            type: item.type === "image" ? "image" : "text",
            variable: item.label || item.key,
          }));
        });
        setIsSaving(false);
        SimpleAlert({
          title: t(
            isCarousel
              ? TEMPLATES_SAVE_VARIABLES_MISMATCH_TITLE_CAROUSEL
              : TEMPLATES_SAVE_VARIABLES_MISMATCH_TITLE
          ),
          text: "",
          icon: "warning",
          width: "min(640px, 94vw)",
          html: createElement(TemplateVariablesMismatchAlertContent, {
            rows,
            description: isCarousel
              ? t(TEMPLATES_SAVE_VARIABLES_MISMATCH_DESCRIPTION_CAROUSEL)
              : undefined,
          }),
        });
        return { ok: false, reason: "variablesMismatch" };
      }
      let transparencyGaps = [];
      try {
        transparencyGaps = await findArtboardsWithTransparencyGaps({
          artboards: checkArtboards,
          assets: assetsRef.current,
        });
        if (isCarousel) {
          transparencyGaps = transparencyGaps.map((gap) => {
            const { slideID } = parseCellKey({ cellKey: gap.artboardID });
            const slideIndex = alignedSlides.findIndex(
              (slide) => slide?.id === slideID
            );
            return {
              ...gap,
              slide:
                slideIndex >= 0
                  ? t(TEMPLATES_SLIDE_LABEL, { index: slideIndex + 1 })
                  : "",
            };
          });
        }
      } catch (error) {
        dispatch(
          LaunchError(error, false, [], {
            functionName: "findArtboardsWithTransparencyGaps",
            capture: "exception",
          })
        );
        console.error("findArtboardsWithTransparencyGaps failed", error);
      }
      if (transparencyGaps.length > 0) {
        setIsSaving(false);
        const confirmed = await ConfirmAlert({
          title: t(TEMPLATES_SAVE_TRANSPARENCY_GAP_TITLE),
          text: "",
          icon: "warning",
          width: "min(560px, 94vw)",
          html: createElement(TemplateTransparencyGapAlertContent, {
            gaps: transparencyGaps,
          }),
          confirmButtonText: t(BUTTON_CONTINUE),
          cancelButtonText: t(BUTTON_GO_BACK),
        });
        if (!confirmed.isConfirmed) {
          return { ok: false, reason: "transparencyGap" };
        }
        setIsSaving(true);
      }
    }
    const response = await dispatch(
      startSaveCreativeTemplate({
        templateID,
        name: nameToSave,
        nodes: graphRef.current.nodes,
        variables: syncVariablesFromArtboards({
          artboards: flushedArtboards,
          variables: variablesRef.current,
        }),
        assets: assetsRef.current,
        artboards: flushedArtboards,
        slides: isCarousel ? alignedSlides : [],
        layoutKind: isCarousel
          ? CREATIVE_TEMPLATE_LAYOUT_KIND.CAROUSEL
          : resolveLayoutKind({
                layoutKind: layoutKindRef.current,
                slides: alignedSlides,
              }) === CREATIVE_TEMPLATE_LAYOUT_KIND.CONTINUOUS_CAROUSEL
            ? CREATIVE_TEMPLATE_LAYOUT_KIND.CONTINUOUS_CAROUSEL
            : CREATIVE_TEMPLATE_LAYOUT_KIND.SINGLE,
        layoutConfigurations:
          layoutKindRef.current ===
          CREATIVE_TEMPLATE_LAYOUT_KIND.CONTINUOUS_CAROUSEL
            ? {
                continuousCarousel: {
                  slideCount: continuousSlideCountRef.current,
                },
              }
            : undefined,
        activeArtboardID: activeArtboardIDRef.current,
        activeSlideID: isCarousel ? activeSlideIDRef.current : undefined,
      })
    );
    if (!silent) setIsSaving(false);
    if (!response.ok) {
      console.error("startSaveCreativeTemplate failed", response);
      SimpleAlert({
        title: t(TEMPLATES_SAVE_ERROR),
        text:
          response.reason === "nameRequired"
            ? t(FIELD_NAME_REQUIRED)
            : response.reason === "unauthorized"
              ? t(TEMPLATES_UNAUTHORIZED)
              : t(TEMPLATES_SAVE_ERROR),
        icon: "error",
      });
      return response;
    }
    if (silent) return response;

    const nodeCount = countAllLayoutNodes({
      layoutKind: layoutKindRef.current,
      slides: alignedSlides,
      artboards: flushedArtboards,
    });
    const isApproved = trimmedName.length > 0 && nodeCount > 0;
    if (closeToProduce) {
      onBackToList();
      return response;
    }
    if (!isApproved) {
      SimpleAlert({
        title: t(TEMPLATES_SAVE_SUCCESS),
        icon: ALERT_ICON_TYPE_SUCCESS,
      });
      return response;
    }

    const confirmed = await ConfirmAlert({
      title: t(TEMPLATES_SAVE_PRODUCE_TITLE),
      text: t(TEMPLATES_SAVE_PRODUCE_DESCRIPTION),
      confirmButtonText: t(TEMPLATES_SAVE_PRODUCE_CONFIRM),
      cancelButtonText: t(TEMPLATES_SAVE_PRODUCE_CANCEL),
      icon: ALERT_ICON_TYPE_SUCCESS,
    });
    if (confirmed.isConfirmed) {
      onBackToList();
      if (!closeToProduce) {
        const produceID = dispatch(getKeyDatabase());
        openTemplateProduce({ templateID, produceID });
      }
      return response;
    }
    return response;
  };

  const getPreviewContext = useCallback(() => {
    const flushed = flushActiveCell({
      layoutKind: layoutKindRef.current,
      slides: slidesRef.current,
      artboards: artboardsRef.current,
      activeSlideID: activeSlideIDRef.current,
      activeArtboardID: activeArtboardIDRef.current,
      nodes: graphRef.current.nodes,
      nodeLayouts: graphRef.current.nodeLayouts,
    });
    const previewSlides = isCarouselLayout({
      layoutKind: layoutKindRef.current,
      slides: flushed.slides,
    })
      ? alignSlideFormats({
          slides: flushed.slides,
          artboards: flushed.artboards,
        })
      : flushed.slides;
    return {
      artboards: flushed.artboards,
      slides: previewSlides,
      layoutKind: layoutKindRef.current,
      layoutConfigurations: {
        continuousCarousel: { slideCount: continuousSlideCountRef.current },
      },
      nodes: graphRef.current.nodes,
      variables: syncVariablesFromArtboards({
        artboards: flushed.artboards,
        variables: variablesRef.current,
      }),
      assets: assetsRef.current,
      activeArtboardID: activeArtboardIDRef.current,
      activeSlideID: activeSlideIDRef.current,
    };
  }, []);

  const {
    previewingKeys,
    isVariablesModalOpen,
    previewColumns,
    previewUrl,
    previewUrls,
    previewVariables,
    onGeneratePreview,
    onCloseVariablesModal,
    onSubmitVariables,
    onClosePreviewImage,
  } = useTemplateEditorPreview({
    templateID,
    getPreviewContext,
    saveTemplate: () => onSave({ silent: true }),
    isLocked,
    initialPreviewProduceID,
    previewRequestKey,
  });
  const isPreviewing = isTemplateEditorPreviewLoading({
    previewingKeys,
    activeSlideID,
    isCarousel,
  });
  const isCarouselPreviewBlocked = previewingKeys.length > 0;
  const hasPreviewableLayers = countNodes({ nodes }) > 0;

  const onCloseEditor = useCallback(async () => {
    const nodeCount = countAllLayoutNodes({
      layoutKind: layoutKindRef.current,
      slides: slidesRef.current,
      artboards: artboardsRef.current,
    });

    if (isProvisionalCreate) {
      const trimmedName = (getValues("name") || "").trim();
      if (!trimmedName && nodeCount === 0) {
        dispatch(
          startDeleteCreativeTemplate({
            templateID,
            mode: CREATIVE_TEMPLATE_DELETE_MODE.DISCARD_DRAFT,
            name: trimmedName,
            nodeCount,
          })
        );
        onBackToList();
        return;
      }
    }

    if (nodeCount > 0) {
      const confirmed = await ConfirmAlert({
        title: t(WARNING),
        text: t(TEMPLATES_CLOSE_UNSAVED_DESCRIPTION),
      });
      if (!confirmed?.isConfirmed) return;
    }

    onBackToList();
  }, [dispatch, getValues, isProvisionalCreate, onBackToList, t, templateID]);

  useEffect(() => {
    onRegisterCloseHandler?.(onCloseEditor);
  }, [onCloseEditor, onRegisterCloseHandler]);

  if (isLoading) {
    return <Loader hasMessage message={t(LOADING_INFORMATION)} />;
  }

  return (
    <ContainerPage
      hasMarginBottom={false}
      sx={{
        height: "100%",
        minHeight: 0,
        flex: 1,
        px: 3,
        pt: 0,
        pb: 3,
        backgroundColor: "background.paper",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          width: "100%",
          flexWrap: "wrap",
          gap: 1.5,
          pt: 3,
          pb: 2,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: 1.5,
            flexWrap: "wrap",
            minWidth: 0,
            flex: 1,
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", sm: TEMPLATE_NAME_AND_LAYERS_WIDTH },
              flexShrink: 0,
              position: "relative",
              "& .MuiFormHelperText-root": {
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                mt: 0,
                mx: 0,
              },
              "& .MuiInputBase-root": {
                height: TEMPLATE_HEADER_CONTROL_HEIGHT,
              },
              "& .MuiFilledInput-input": {
                paddingTop: 0,
                paddingBottom: 0,
              },
            }}
          >
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: "text.secondary",
                mb: 0.5,
              }}
            >
              {t(TEMPLATES_NAME)}
            </Typography>
            <TextField
              control={control}
              errors={errors}
              name="name"
              variant="filled"
              fullWidth
              disabled={!canWrite || (!isCarousel && isAnalyzing)}
              rules={{ required: t(FIELD_NAME_REQUIRED) }}
            />
          </Box>
          <Box
            sx={{
              width: { xs: "100%", sm: 220 },
              flexShrink: 0,
              mb: "2px",
              "& > [role='radiogroup']": {
                height: TEMPLATE_HEADER_CONTROL_HEIGHT,
                boxSizing: "border-box",
                alignItems: "stretch",
              },
              "& > [role='radiogroup'] > label": {
                height: "100%",
                minHeight: 0,
              },
            }}
          >
            <SegmentedControl
              name="templateLayoutKind"
              options={layoutKindOptions}
              selectedValue={selectedLayoutKind}
              onChange={onChangeLayoutKind}
              disabled={!canChangeLayoutKind}
            />
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={onUpload}
          />
          <input
            ref={referenceFileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={onUploadReference}
          />
          <Button
            iconOnly
            color="secondary"
            ariaLabel={t(TEMPLATES_USAGE_GUIDE)}
            tooltip={t(TEMPLATES_USAGE_GUIDE)}
            onClick={() => setIsGuideVideoOpen(true)}
          >
            <HelpOutlineIcon />
          </Button>
          <Tooltip
            title={
              isTemplateProducing ? t(TEMPLATES_SAVE_BLOCKED_PRODUCING) : ""
            }
          >
            <span>
              <Button
                color="secondary"
                onClick={() => onSave()}
                disabled={isLocked || isTemplateProducing}
                loading={isSaving}
              >
                {t(TEMPLATES_SAVE)}
              </Button>
            </span>
          </Tooltip>
          <IconButton
            className="close-modal-button"
            aria-label={t(BUTTON_CLOSE)}
            onClick={onCloseEditor}
            sx={{
              p: 0.5,
              fontSize: "1.4rem",
              color: "background.paperSecondaryContrastText",
              backgroundColor: "background.paperSecondary",
              "&:hover": {
                color: "background.paperSecondaryContrastText",
                backgroundColor: "background.paperSecondary",
                filter: theme.palette.hover.main,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>
      {isLocked ? (
        <Typography sx={{ mb: 2, flexShrink: 0 }} color="warning.main">
          {t(lockMessage)}
        </Typography>
      ) : null}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: `${layersSidebarWidth}px minmax(0, 1fr) 320px`,
          },
          gap: 2,
          width: "100%",
          flex: 1,
          minHeight: 0,
          alignItems: "stretch",
          overflow: { xs: "auto", md: "hidden" },
        }}
      >
        <Box
          sx={{
            minHeight: { xs: 360, md: 0 },
            height: { xs: "auto", md: "100%" },
            position: "relative",
          }}
        >
          <LayerTreePanel
            nodes={nodes}
            nodeLayouts={nodeLayouts}
            assets={assets}
            tryingNodeIDs={{ ...tryingNodeIDs, ...resizingBackgroundNodeIDs }}
            selectedID={selectedID}
            selectedIDs={selectedIDs}
            expandedIDs={expandedIDs}
            disabled={isLocked}
            onSelect={onSelect}
            onToggleExpand={(id) =>
              setExpandedIDs((current) => ({
                ...current,
                [id]: !current[id],
              }))
            }
            onRename={(id, name) => {
              const trimmed = String(name || "").trim();
              if (!trimmed) return;
              const found = findNodePath({
                nodes: graphRef.current.nodes,
                nodeID: id,
              });
              if (!found) return;
              const patch = { name: trimmed };
              if (found.node.source?.kind === "data") {
                patch.source = {
                  ...found.node.source,
                  kind: "data",
                  variable: slugifyName({ name: trimmed, fallback: id }),
                };
              }
              push();
              commitGraph({
                nodes: patchNodeById({
                  nodes: graphRef.current.nodes,
                  nodeID: id,
                  patch,
                }),
              });
            }}
            onReorder={({ parentID, orderedDisplayIds }) => {
              push();
              commitGraph(
                applySiblingDisplayOrder({
                  nodes: graphRef.current.nodes,
                  nodeLayouts: graphRef.current.nodeLayouts,
                  parentID,
                  orderedDisplayIds,
                })
              );
            }}
            onDelete={(id) => {
              push();
              commitGraph(
                deleteNodeById({
                  nodes: graphRef.current.nodes,
                  nodeLayouts: graphRef.current.nodeLayouts,
                  nodeID: id,
                })
              );
              if (selectedID === id || selectedIDs.includes(id)) {
                const next = selectedIDs.filter((item) => item !== id);
                applySelection(next[next.length - 1] || null, next);
              }
            }}
            onCopy={(id) => {
              if (!copySelectedLayer(id)) return;
              pasteCopiedLayer();
            }}
            onToggleLock={onToggleLock}
            onToggleVisible={onToggleVisible}
            onAddLayer={onAddLayer}
          />
          <Box
            onPointerDown={onResizeLayersSidebar}
            sx={{
              ...sidebarResizeHandleSx,
              display: { xs: "none", md: "block" },
            }}
          />
        </Box>
        <Box
          sx={{
            bgcolor: "#fff",
            border: "1px solid #E4E4E7",
            borderRadius: "14px",
            p: 2,
            minHeight: 0,
            height: { xs: "auto", md: "100%" },
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1,
              mb: 1.5,
              flexWrap: "wrap",
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 1,
                minWidth: 0,
              }}
            >
              {isCarouselLayout({ layoutKind, slides }) ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: 0.5,
                  }}
                >
                  <SlideSwitcher
                    slides={slides}
                    activeSlideID={activeSlideID}
                    disabled={!canWrite}
                    analyzingSlideIDs={analyzingSlideIDs}
                    uploadingGuideSlideIDs={uploadingGuideSlideIDs}
                    onSelect={onSelectSlide}
                    onReorder={onReorderSlides}
                    onDuplicate={onDuplicateSlide}
                    onDelete={onDeleteSlide}
                    onAdd={onAddSlide}
                  />
                </Box>
              ) : null}
              {isContinuous ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: 0.5,
                  }}
                >
                  <ContinuousSlideChips
                    slideCount={continuousSlideCount}
                    disabled={!canWrite || isLocked}
                    canAdd={
                      continuousSlideCount <
                      CREATIVE_TEMPLATE_CONTINUOUS_SLIDE_MAX
                    }
                    canRemove={
                      continuousSlideCount >
                      CREATIVE_TEMPLATE_CONTINUOUS_SLIDE_MIN
                    }
                    onCenter={onCenterStripSlide}
                    onAdd={onAddStripSlide}
                    onRemove={onRemoveStripSlide}
                  />
                </Box>
              ) : null}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 0.5,
                }}
              >
                {isCarouselLayout({ layoutKind, slides }) ? (
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: "text.secondary",
                      letterSpacing: 0.2,
                      lineHeight: 1.2,
                    }}
                  >
                    {t(TEMPLATES_FORMATS_ROW)}
                  </Typography>
                ) : null}
                <ArtboardSwitcher
                  artboards={formatSwitcherArtboards}
                  activeArtboardID={activeArtboardID}
                  disabled={isLocked}
                  onSelect={onSelectArtboard}
                  onBeforeAddFormat={onRequestAddFormat}
                  onAddPreset={onAddArtboardPreset}
                  onAddCustom={onAddCustomArtboard}
                  onRemoveActive={onRemoveActiveArtboard}
                  continuousSlideCount={isContinuous ? continuousSlideCount : 0}
                />
                <ContinuousSetupModal
                  open={isContinuousSetupOpen}
                  onClose={() => setIsContinuousSetupOpen(false)}
                  onConfirm={onConfirmContinuousSetup}
                />
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                ml: "auto",
              }}
            >
              {continuousModeSwitch}
              <Tooltip title={t(TEMPLATES_ALIGNMENT_GUIDES)}>
                <span>
                  <IconButton
                    size="small"
                    aria-label={t(TEMPLATES_ALIGNMENT_GUIDES)}
                    onClick={() => setAlignmentGuidesEnabled((value) => !value)}
                    sx={{
                      width: 32,
                      height: 32,
                      border: "1px solid #E4E4E7",
                      borderRadius: BORDER_RADIUS_BUTTON,
                      bgcolor: "#fff",
                      color: "text.primary",
                    }}
                  >
                    {alignmentGuidesEnabled ? (
                      <Magnet size={16} />
                    ) : (
                      <MagnetOffIcon size={16} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <GuideOpacityControl
                opacity={guideOpacity}
                disabled={!canvasImageUrl}
                onOpen={() => {
                  if (guideOpacity > 0) setIsGuideVisible(true);
                }}
                onChange={(next) => {
                  setGuideOpacity(next);
                  setIsGuideVisible(next > 0);
                }}
              />
              <Button
                color="secondary"
                tooltip={t(TEMPLATES_GUIDE_HINT)}
                onClick={() => referenceFileInputRef.current?.click()}
                loading={isUploadingGuide}
                disabled={isLocked}
                sx={{
                  height: 32,
                  minWidth: 0,
                  px: 1.5,
                  py: 0,
                  fontSize: "13px !important",
                }}
              >
                {t(
                  canvasImageUrl
                    ? TEMPLATES_CHANGE_REFERENCE
                    : TEMPLATES_UPLOAD_REFERENCE
                )}
              </Button>
              {hasPreviewableLayers ? (
                isCarousel ? (
                  <ButtonMenu
                    mode="button"
                    colorButton="secondary"
                    textButton={TEMPLATES_PRODUCE_PREVIEW}
                    tooltipText={TEMPLATES_PRODUCE_PREVIEW_TOOLTIP}
                    loading={isPreviewing}
                    disabled={isLocked || isPreviewing}
                    sx={{
                      height: 32,
                      minWidth: 0,
                      px: 1.5,
                      py: 0,
                      fontSize: "13px !important",
                    }}
                    options={[
                      {
                        label: TEMPLATES_PRODUCE_PREVIEW_THIS_SLIDE,
                        onClick: () =>
                          onGeneratePreview(
                            TEMPLATE_EDITOR_PREVIEW_SCOPE.SLIDE
                          ),
                      },
                      {
                        label: TEMPLATES_PRODUCE_PREVIEW_WHOLE_CAROUSEL,
                        disabled: isCarouselPreviewBlocked,
                        onClick: () =>
                          onGeneratePreview(
                            TEMPLATE_EDITOR_PREVIEW_SCOPE.CAROUSEL
                          ),
                      },
                    ]}
                  />
                ) : (
                  <Button
                    color="secondary"
                    tooltip={t(TEMPLATES_PRODUCE_PREVIEW_TOOLTIP)}
                    onClick={() =>
                      onGeneratePreview(TEMPLATE_EDITOR_PREVIEW_SCOPE.SLIDE)
                    }
                    loading={isPreviewing}
                    disabled={isLocked || isPreviewing}
                    sx={{
                      height: 32,
                      minWidth: 0,
                      px: 1.5,
                      py: 0,
                      fontSize: "13px !important",
                    }}
                  >
                    {t(TEMPLATES_PRODUCE_PREVIEW)}
                  </Button>
                )
              ) : null}
            </Box>
          </Box>
          {isEmptyCanvas ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 360,
                border: "1px dashed #D4D4D8",
                borderRadius: "12px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                px: 3,
                textAlign: "center",
                bgcolor: "#FAFAFA",
              }}
            >
              <Typography color="text.secondary" sx={{ maxWidth: 420 }}>
                {t(
                  showNewFormatEmpty
                    ? TEMPLATES_NEW_FORMAT_HINT
                    : TEMPLATES_EMPTY_CANVAS_HINT
                )}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                  justifyContent: "center",
                }}
              >
                {showNewFormatEmpty ? (
                  <>
                    <Button
                      color="secondary"
                      onClick={onCopyPreviousFormat}
                      disabled={!canWrite || isLocked}
                    >
                      {t(TEMPLATES_EDIT_PREVIOUS)}
                    </Button>
                    <ButtonMenu
                      mode="button"
                      colorButton="primary"
                      textButton={TEMPLATES_BUTTON_UPLOAD}
                      loading={isUploading}
                      disabled={!canWrite}
                      options={[
                        {
                          label: TEMPLATES_UPLOAD_FROM_DEVICE,
                          onClick: () => fileInputRef.current?.click(),
                        },
                        {
                          label: TEMPLATES_UPLOAD_FROM_GALLERY,
                          onClick: () => setIsCreativeGalleryOpen(true),
                        },
                      ]}
                    />
                  </>
                ) : (
                  <>
                    <Button
                      color="secondary"
                      onClick={onAddFullCanvasLayer}
                      disabled={!canWrite || isLocked}
                    >
                      {t(TEMPLATES_EMPTY_ADD_LAYER)}
                    </Button>
                    <ButtonMenu
                      mode="button"
                      colorButton="primary"
                      textButton={TEMPLATES_BUTTON_UPLOAD}
                      loading={isUploading}
                      disabled={!canWrite}
                      options={[
                        {
                          label: TEMPLATES_UPLOAD_FROM_DEVICE,
                          onClick: () => fileInputRef.current?.click(),
                        },
                        {
                          label: TEMPLATES_UPLOAD_FROM_GALLERY,
                          onClick: () => setIsCreativeGalleryOpen(true),
                        },
                      ]}
                    />
                  </>
                )}
              </Box>
            </Box>
          ) : (
            <Box data-template-canvas sx={{ flex: 1, minHeight: 0 }}>
              <TemplateCanvasOverlay
                imageUrl={canvasImageUrl}
                showGuide={isGuideVisible}
                guideOpacity={guideOpacity}
                alignmentGuidesEnabled={alignmentGuidesEnabled}
                artboardWidth={artboard.width}
                artboardHeight={artboard.height}
                boxes={boxes}
                tryingNodeIDs={tryingNodeIDs}
                selectedID={selectedID}
                selectedIDs={selectedIDs}
                editingTextID={editingTextID}
                onTextSelectionChange={onTextSelectionChange}
                selectedTextNodeID={textSelection?.nodeID || null}
                selectedTextPhrase={textSelection?.phrase || ""}
                isDrawing={false}
                isLocked={isLocked}
                isScanning={isAnalyzing}
                analysisError={
                  isAnalysisError ? t(TEMPLATES_ANALYZE_ERROR) : ""
                }
                retryLabel={t(TEMPLATES_ANALYZE_RETRY)}
                onRetry={
                  canWrite && !isAnalyzing
                    ? () => onAnalyzeCreative()
                    : undefined
                }
                onSelectBox={(id, event) =>
                  onSelect(id, event, { preserveMulti: true })
                }
                onSelectIds={onSelectIds}
                onCreateBox={onCreateBox}
                onUpdateBox={onUpdateBox}
                onUpdateBoxes={onUpdateBoxes}
                onEndBoxTransform={endCoalesce}
                onStartTextEdit={setEditingTextID}
                onTextCommit={onCommitFixedTextStable}
                seamOffsets={seamOffsets}
                canvasApiRef={canvasApiRef}
              />
            </Box>
          )}
        </Box>
        <Box
          sx={{
            minHeight: { xs: 360, md: 0 },
            height: { xs: "auto", md: "100%" },
          }}
        >
          <LayerPropertiesPanel
            control={control}
            errors={errors}
            disabled={isLocked || !selectedNode}
            selectedNode={selectedNode}
            isRootLayer={!selectedPath?.parentID}
            assetUrl={selectedAssetUrl}
            nameSuggestions={nameSuggestions}
            isTextRangeSelected={isTextRangeSelected}
            onChangeField={onChangeFieldFromInspector}
            onSelectAsset={() =>
              onOpenAssetsGallery(ASSETS_GALLERY_MODE.REPLACE)
            }
            onSelectInlineReplacementAsset={(slotID) => {
              inlineReplacementSlotIDRef.current = slotID;
              onOpenAssetsGallery(ASSETS_GALLERY_MODE.INLINE_REPLACEMENT);
            }}
            inlineReplacementUrls={selectedInlineReplacementUrls}
            onTryGenerate={onTryGenerate}
            isTryingGenerate={Boolean(tryingLayerKeys[selectedTryKey])}
            onGeneratePrompt={onGenerateLayerPrompt}
            isGeneratingPrompt={Boolean(
              generatingPromptKeys[selectedPromptGenerationKey]
            )}
            originImageUrl={selectedOriginImageUrl}
            alignOnly={selectedIDs.length >= 2}
            onAlign={onAlignLayers}
          />
        </Box>
      </Box>
      {isAssetsGalleryOpen ? (
        <ModalGalleryAssets
          modalOpen={isAssetsGalleryOpen}
          multiple={assetsGalleryMode === ASSETS_GALLERY_MODE.ADD}
          onCloseModal={() => {
            inlineReplacementSlotIDRef.current = null;
            setIsAssetsGalleryOpen(false);
          }}
          onCallbackSelectedItems={onSelectAssetsGalleryItems}
        />
      ) : null}
      {isCreativeGalleryOpen ? (
        <ModalGallery
          modalOpen={isCreativeGalleryOpen}
          multiple={false}
          onCloseModal={() => setIsCreativeGalleryOpen(false)}
          onCallbackSelectedItems={onSelectCreativeFromGallery}
          acceptedFormats={IMAGE_FORMATS}
          pathStorage={`socialMedia/${userID}/`}
          modeUpload="sync"
          showUploadDeviceOption={false}
          enableHistoryManagement={false}
        />
      ) : null}
      {isVariablesModalOpen ? (
        <TemplateEditorPreviewVariablesModal
          open={isVariablesModalOpen}
          columns={previewColumns}
          onClose={onCloseVariablesModal}
          onSubmit={onSubmitVariables}
        />
      ) : null}
      {isGuideVideoOpen ? (
        <ModalWatchGuideVideo
          modalOpen={isGuideVideoOpen}
          videoUrl={t(TEMPLATES_GUIDE_VIDEO_URL)}
          onCloseModal={() => setIsGuideVideoOpen(false)}
        />
      ) : null}
      {previewUrl ? (
        <TemplateEditorPreviewImageModal
          open={Boolean(previewUrl)}
          url={previewUrl}
          urls={previewUrls}
          variables={previewVariables}
          onClose={onClosePreviewImage}
        />
      ) : null}
      {tryImageModal ? (
        <TemplateEditorPreviewImageModal
          open
          url={tryImageModal.url}
          variables={[
            {
              key: "prompt",
              label: t(TEMPLATES_PROMPT),
              type: "string",
              value: tryImageModal.prompt,
            },
          ]}
          showSave={false}
          onClose={onCloseTryImageModal}
        />
      ) : null}
    </ContainerPage>
  );
};

export default TemplateEditor;
